/**
 * Temas de ayuda · Ventas: POS, documentos, cotizaciones, clientes, Zelle y Binance, devoluciones. La tasa 2 y la regla
 * del cobro van en bloques que solo ve la dueña (`VE_TASA_2`), con su versión sin tasa para los demás (`SIN_TASA_2`).
 */
import { SIN_TASA_2, VE_TASA_2, type TemaAyuda } from '../tipos.ts';

export const TEMAS_VENTAS: TemaAyuda[] = [
  // ─── Ventas (POS) ───────────────────────────────────────────────────────────
  {
    id: 'pos',
    titulo: 'Ventas (POS)',
    rutas: ['/pos'],
    permiso: { clave: 'ventas.emitir', nivel: 'total' },
    resumen: 'Aquí se arma y se cobra cada venta: factura o nota de entrega, siempre a un cliente identificado. Atajos: **F2** cliente, **F4** cobrar, **F9** espera.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'La franja de la caja',
        items: [
          '**Verde**, «Caja … abierta desde …»: puedes vender. Ahí mismo tienes **Retiro de efectivo** y **Contar y cerrar caja**, que llevan a **Caja**.',
          '**Ámbar**, «No tienes una caja abierta»: pulsa **Abrir caja**, elige tu caja, confirma el fondo y pulsa **Abrir caja y empezar a vender**. No sales de la venta.',
          '**Roja**, «… quedó abierta desde el …»: la caja es de un día anterior. Pulsa **Contar y cerrar caja** y después abre la de hoy.',
          'Sin caja puedes armar la venta, pero no cobrarla. Si pulsas **Cobrar**, sale el formulario para abrir la caja y el carrito no se pierde.',
          'Si eres cajero y tu caja está cerrada, la pantalla muestra **Tu caja está cerrada** con el formulario para abrirla.',
          'Si ya declaraste el conteo, sale **Tu caja está en cierre**: pulsa **Ir a Caja** y termina el cierre.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Hacer una venta',
        pasos: [
          '**Cliente**: pulsa **F2** (o haz clic en el campo del cliente). Se despliega la lista: escribe nombre, cédula o RIF y elígelo. **Esc** cierra la lista.',
          'Si el cliente no existe, pulsa **Nuevo** y regístralo ahí mismo: queda elegido y el carrito sigue igual.',
          '**Tipo**: **Factura** (fiscal, con IVA y número de control) o **Nota** (nota de entrega, no fiscal). Después de cada venta vuelve a **Factura**.',
          '**Productos**: escribe código, nombre o marca en el buscador. **Enter** agrega el primero; o pulsa la presentación que quieras (unidad, docena, caja). Si hay más resultados de los que caben, abajo dice cuántos son y con **Anterior** y **Siguiente** se ven los demás; **Enter** agrega el primero de la página que se ve.',
          '**Cantidad**: cámbiala en la columna **Cant.** Cada producto tiene su fracción mínima: unos se venden enteros, otros por medios o por decimales.',
          'Si el cliente es contribuyente especial o municipal y es factura, elige la **Modalidad de retención…**. Sin ella no se puede cobrar.',
          'Revisa los totales y pulsa **F4 · Cobrar** (o la tecla **F4**). Se abre la ventana de cobro.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Lo que muestra la pantalla',
        items: [
          'Cada producto dice lo disponible en la tienda y en cada depósito. Cada presentación trae dos precios igual de visibles: en verde el de **divisa** y en azul el de tasa **BCV**.',
          'Cada renglón del carrito muestra el precio y el subtotal en **Bs · $ a BCV · $**: el subtotal es el precio por la cantidad. Los precios vienen de la lista del cliente y nadie los cambia en la venta.',
          'Abajo salen la base y el IVA. En una factura de contado, además, lo que pagaría el cliente **Todo en bolívares** y **Todo en dólares** (con el IGTF): son los mismos montos del cobro.',
          'El botón verde dice lo que se cobra: **F4 · Cobrar $ … · Bs …**. Si pulsas **F4** mientras se recalcula, el cobro se abre apenas termina, con el total nuevo.',
          'Un servicio (corte de lámina, flete) dice «servicio (sin inventario)»: lleva IVA en la factura y no queda nada por entregar.',
          '**Limpiar** vacía el carrito.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Combos y recetas',
        items: [
          'Pulsa **Combo o receta** junto al buscador, elige el combo y escribe cuántos combos o la medida del trabajo (por ejemplo 26 m²). Pulsa **Agregar al carrito**: entran todos sus productos con sus cantidades y su descuento.',
          'En el carrito el combo es un bloque verde. **Cambiar medida** recalcula y conserva el descuento. **Desarmar**, o quitar uno de sus productos, deja los productos sueltos y sin el descuento del combo. **Quitar combo** los quita todos.',
          'El descuento del combo no pide autorización y solo vale con el combo completo.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Piezas de vidrio y productos con detalles',
        items: [
          'Pulsa **Piezas de vidrio** o **Con detalles** junto al buscador (o escribe «piezas» o «produc»). Sale todo lo disponible, cada uno con su precio.',
          'Pulsa el precio para agregarlo. En **Cant.** van unidades enteras, hasta las que quedan.',
          'Al buscar un nombre («vidrio claro») también salen, debajo de los productos, las piezas y los productos con detalles que coinciden.',
          'Si otra caja se lleva las últimas, el renglón queda en rojo con lo que queda.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Vender desde un depósito',
        pasos: [
          'Cuando la tienda no alcanza, el renglón se marca en rojo y aparece **Vender desde:** con los depósitos que sí tienen.',
          'Pulsa el depósito. El renglón pasa a decir **Sale de …** ese depósito.',
          'Si tu usuario no tiene el permiso, el carrito avisa que se pedirá autorización al cobrar.',
          'Para deshacerlo, pulsa **volver a la tienda**. El cliente retira en ese depósito.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Descuentos',
        items: [
          'Los **descuentos programados** de la dueña se aplican solos, sin clave. El renglón dice el porcentaje y el nombre del descuento.',
          '**Desc. %** es un descuento para toda la venta. Si tu usuario no puede dar descuentos, dice **precios fijos** y **Desc. % (autoriz.)**: lo puedes escribir, pero al cobrar se pide autorización.',
          'Si un producto tiene descuento programado y además se escribe uno manual, rige el mayor: no se suman.',
          'El renglón que trae el descuento de una cotización lo dice, con el enlace **quitar**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Avisos del carrito',
        items: [
          'Consumidor Final sale marcado **Solo para cotizar**: sirve para ver precios, pero no deja cobrar.',
          'Cliente con deuda vencida: el carrito dice cuánto debe vencido. Para venderle, de contado o a crédito, se pide autorización.',
          'Nota a un cliente sin teléfono: complétalo en su ficha antes de cobrar.',
          'Talonario de facturas: con 20 hojas o menos sale una franja ámbar. Agotado, la franja es roja y la factura no se cobra (la nota sí sale). La pantalla se entera sola cuando la dueña carga el talonario nuevo.',
          '**La tasa cambió**: la dueña registró otra tasa y el carrito ya se recalculó. Revisa el total antes de cobrar.',
          'En una caja **Solo facturas** solo aparece **Factura**.',
        ],
      },
      { tipo: 'nota', texto: 'Debajo de **Todo en bolívares** ves además su referencial a BCV. Solo tú lo ves: deja ver la regla de la tasa 2.', permiso: VE_TASA_2 },
      { tipo: 'nota', tono: 'aviso', texto: 'Una factura cabe en una sola hoja. Si tiene más renglones de los que caben en el papel de la sucursal, el sistema pide dividir la venta en dos facturas. La nota de entrega no tiene ese tope.' },
      {
        tipo: 'lista',
        titulo: 'Después de cobrar',
        items: [
          'Sale una ventana con el número (y el número de control si es factura), el total y el vuelto.',
          '**Imprimir** abre el formato en otra pestaña. **Ver documento** abre el detalle. **Nueva venta** deja la pantalla lista para la siguiente.',
          'Si la venta vino de una cotización, la franja de arriba lo dice y la cotización queda **Facturada** al cobrar. **desvincular** la separa de esta venta.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'No se pudo cargar · No se pudo conectar con el servidor', que_hacer: 'Se cayó la conexión: el producto no desapareció. Pulsa **Reintentar** cuando vuelva; no hace falta recargar la página.' },
      { mensaje: 'Consumidor Final es solo para cotizaciones: elige o registra al cliente', que_hacer: 'Pulsa **F2** y elige al cliente, o créalo con **Nuevo**.' },
      { mensaje: 'Para emitir FACTURA el cliente necesita RIF y dirección fiscal', que_hacer: 'Completa la ficha del cliente, o emite una nota.' },
      { mensaje: 'Para la nota de entrega hace falta el teléfono de …', que_hacer: 'Completa el teléfono en la ficha del cliente.' },
      { mensaje: 'La lista de precios «…» de … está desactivada: cámbiale la lista en su ficha (Lista y crédito) antes de venderle', que_hacer: 'De una lista desactivada no sale ningún precio: quien administra el crédito le elige otra lista en la ficha del cliente.' },
      { mensaje: '… se vende en múltiplos de …', que_hacer: 'Corrige la cantidad.' },
      { mensaje: 'Disponible insuficiente en el almacén principal', que_hacer: 'Baja la cantidad, o usa **Vender desde:** un depósito.' },
      { mensaje: 'La factura tiene … renglones y en la hoja de … caben …: divide la venta en dos facturas', que_hacer: 'Haz dos facturas.' },
      { mensaje: 'Quedan … hojas del talonario de facturas', que_hacer: 'Avisa a la dueña para que cargue el talonario nuevo.' },
      { mensaje: 'Se agotó el talonario de facturas', que_hacer: 'No se factura hasta que la dueña cargue el talonario nuevo en **Configuración**. La nota de entrega sí sale.' },
      { mensaje: 'La tasa cambió', que_hacer: 'El carrito ya se recalculó: revisa el total antes de cobrar.' },
      { mensaje: 'No hay ninguna tasa registrada: la dueña debe cargar la tasa BCV y la tasa 2 en Tasas antes de vender o comprar', que_hacer: 'Avisa a la dueña: registra las tasas en **Configuración**, en **Tasas**.' },
      { mensaje: 'Esta venta ya se cobró en otra pantalla', que_hacer: 'La venta en espera ya salió con otro documento. No la cobres otra vez: limpia el carrito.' },
      { mensaje: 'No se pudo emitir', que_hacer: 'Si se cortó la conexión, pulsa **Cobrar** otra vez sin cambiar nada: no sale una segunda factura.' },
      { mensaje: '… ya había quedado emitida en un intento anterior', que_hacer: 'El primer intento sí salió y el carrito cambió después. Revisa ese documento y cobra solo lo que falte.' },
      { mensaje: 'Abre tu caja para cobrar', que_hacer: 'Ábrela en la ventana que aparece. Lo que tienes en el carrito no se pierde.' },
      { mensaje: 'Tu caja quedó abierta de otro día', que_hacer: 'Pulsa **Contar y cerrar caja**, ciérrala y después abre la de hoy.' },
      { mensaje: 'Tu caja abierta es la … de …', que_hacer: 'Tu caja está abierta en otra sucursal: ciérrala allá para poder cobrar aquí.' },
      { mensaje: 'La cotización … trae descuento … Tu usuario no puede dar descuentos', que_hacer: 'Pide la autorización al cobrar, o pulsa **Quitar el descuento y facturar a precio completo**.' },
      { mensaje: 'Estás en la consola global', que_hacer: 'Elige la sucursal que vas a operar en la misma pantalla o con **Elegir sucursal** en la cabecera.' },
    ],
    manual: '4',
    relacionados: ['caja', 'clientes', 'cotizaciones', 'documentos', 'autorizacion', 'impresion', 'combos.agregar'],
    claves: ['vender', 'venta', 'factura', 'nota de entrega', 'carrito', 'F2', 'F4', 'F9', 'depósito', 'descuento', 'piezas de vidrio', 'con detalles', 'talonario', 'tasa cambió', 'consumidor final', 'abrir caja'],
  },
  {
    id: 'pos.cobro',
    titulo: 'Cobrar (F4)',
    padre: 'pos',
    resumen: 'La ventana de cobro. A la izquierda, la factura o la nota. A la derecha, cuánto paga el cliente según cómo pague, las formas de pago y lo que falta o sobra.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cobrar una venta',
        pasos: [
          'Pulsa **F4 · Cobrar**. La primera forma de pago viene en **Efectivo $** con el monto ya puesto.',
          'Si paga de otra forma, cambia el método: **Efectivo Bs**, **Zelle**, **USDT**, **Punto de venta**, **Transferencia**, **Pago móvil** o **Saldo a favor** (solo si el cliente tiene).',
          'Para combinar, pulsa **Agregar forma de pago**. Al cambiar de método o agregar una línea, el monto se pone solo con lo que falta en esa moneda.',
          '**Resto** vuelve a poner en esa línea lo que falta.',
          'En efectivo ($ o Bs) no hay campo de referencia: el monto ocupa ese lugar y se lee completo.',
          'Escribe la referencia donde el método la pide.',
          'Mira **Falta**: cuando llega a cero, pulsa **Cobrar**. El botón dice lo que se cobra: **Cobrar $ … (con IGTF)**, o **Cobrar Bs …** cuando se cobra en bolívares.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Cuánto paga según cómo pague (factura de contado)',
        items: [
          '**Si paga en bolívares**: el monto en Bs.',
          '**Si paga en dólares**: el monto en $, con el IGTF.',
          'La forma que corresponde a lo que escribiste dice **Así está cobrando**. Si paga una parte en dólares y el resto en bolívares, cobra lo que dicen **Falta** y **Resto**.',
        ],
        permiso: SIN_TASA_2,
      },
      {
        tipo: 'lista',
        titulo: 'Cuánto paga según cómo pague (factura de contado)',
        items: [
          '**Todo en bolívares**: a tasa 2, con su referencial a BCV.',
          '**Con dólares**: al precio real a tasa BCV, más el IGTF. Si lo que falta del precio sin IVA no pasa del tope (20 $), ese resto se paga en bolívares a tasa BCV.',
          '**Con dólares, faltando más de …**: sale solo si la factura pasa del tope sin IVA. Va a tasa 2 y cada dólar cuenta a tasa BCV.',
          'La forma que corresponde a lo que escribiste dice **Así está cobrando**. Encima de la factura dice a qué tasa están los bolívares.',
          'Solo tú ves la tasa 2, las tres reglas, el referencial de lo que falta y el tope de bolívares del día. El cajero y los demás ven **Si paga en bolívares** y **Si paga en dólares** con sus montos y lo que falta.',
        ],
        permiso: VE_TASA_2,
      },
      {
        tipo: 'lista',
        titulo: 'La factura de la izquierda',
        items: [
          'Cada producto con su cantidad, su precio y su subtotal. Si son muchos, la lista se desplaza.',
          'Debajo: **Precio sin IVA**, **IVA 16 %**, las retenciones si el cliente retiene, el **IGTF** (cuánto y sobre qué) y el **Total a cobrar**.',
          'Cobrando en bolívares, todo va en bolívares y el dólar sale una sola vez: el **Referencial a BCV**, debajo del total. Es lo que le dices al cliente.',
          'Cada producto dice en letra chica su **ref. $**, por si el cliente pregunta. Lo que se cobra es el referencial del total.',
          'Debajo de cada monto en bolívares que escribes dice cuánto es en dólares a BCV (**ref. $ … a BCV**).',
          'Pagando con dólares a tasa BCV hay dos columnas: los dólares de cada producto y los bolívares de la factura.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Referencias',
        items: [
          '**Punto de venta**: los últimos 4 dígitos del comprobante. No se repite en el mismo día y sucursal.',
          '**Transferencia**, **Pago móvil**, **Zelle** y **USDT**: el mínimo de caracteres que dice el campo. No se repiten nunca. Guiones, espacios y mayúsculas no cuentan.',
          'Transferencia y pago móvil comparten referencias. Zelle y USDT también.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Saldo a favor',
        items: [
          '**Saldo a favor**: al elegirlo, el monto se pone solo, hasta cubrir lo que falta. Lo que no se use sigue en el saldo del cliente.',
          'En una factura o una nota de débito, la parte del saldo que entró con abonos en divisas cuenta como un pago en dólares y lleva su **IGTF**. Esa parte se gasta primero.',
          'El saldo de la devolución de una factura, de un reintegro de retención o de un abono anterior al 19/10/2026 no lleva IGTF. El de la devolución de una nota pagada en efectivo $, Zelle, USDT o con esa parte del saldo sí, como el abono: la nota nunca pagó IGTF.',
          'Una nota de entrega pagada con saldo nunca lleva IGTF.',
          'Si otra caja usa el saldo del mismo cliente mientras cobras, el cobro te pide volver a abrirlo: no se cobró nada.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Bolívares en notas',
        items: [
          'En notas, cada cliente tiene un tope de bolívares al día entre todas sus notas, y un máximo de 3 notas con bolívares. Si se pasa, el cobro lo avisa: cobra el resto en dólares.',
          'Si el cliente ya llegó al tope de hoy, un aviso en ámbar lo dice antes de cobrar (en el carrito, en el cobro y en la diferencia de un cambio): cobra en efectivo $, Zelle o USDT.',
          'Las facturas no tienen ese tope.',
        ],
        permiso: SIN_TASA_2,
      },
      {
        tipo: 'lista',
        titulo: 'Bolívares en notas',
        items: [
          'En notas, un cliente paga en bolívares hasta el equivalente de 20 $ al día, entre todas sus notas, y en un máximo de 3 notas. **Falta** te dice **Bs hoy: hasta …** (los demás no lo ven: el cobro los avisa si se pasan).',
          'Al llegar al máximo, la nota se cobra en dólares. Las facturas no tienen ese tope.',
          'Si el cliente ya llegó al tope de hoy, todos ven un aviso en ámbar antes de cobrar (en el carrito, en el cobro y en la diferencia de un cambio), en dólares y sin el tope en Bs.',
        ],
        permiso: VE_TASA_2,
      },
      { tipo: 'nota', texto: 'Desde **Documentos** (cobrar un saldo) o **Devoluciones** (la diferencia) la ventana tiene una sola columna: solo lo que se cobra.' },
      { tipo: 'nota', tono: 'aviso', texto: 'Si sale «No se pudo emitir» porque se cortó la conexión, no armes la venta de nuevo: pulsa **Cobrar** otra vez. No sale una segunda factura.' },
    ],
    mensajes: [
      { mensaje: '… la referencia debe tener al menos … caracteres', que_hacer: 'Copia la referencia completa.' },
      { mensaje: 'La referencia … ya fue usada en … (tienda, fecha, monto): un mismo pago no se cobra dos veces', que_hacer: 'Ese pago ya se cobró en el documento que dice el mensaje. Si el cliente hizo otro pago, pídele su referencia.' },
      { mensaje: 'El pago … no cubre el monto a cobrar', que_hacer: 'Falta dinero o falta el IGTF: cobra el monto que se pone solo (botón **Resto**).' },
      { mensaje: 'En NOTAS la porción en Bs no puede superar …', que_hacer: 'Cobra el resto en dólares.' },
      { mensaje: 'Este cliente ya llegó al máximo de notas con pago en bolívares de hoy', que_hacer: 'Cobra esta nota en dólares.' },
      { mensaje: 'Saldo a favor insuficiente: disponible …', que_hacer: 'Baja el monto del saldo a favor.' },
      { mensaje: 'Con estos montos no hay uno exacto en dólares…', que_hacer: 'Baja los bolívares o cobra el resto en bolívares.' },
      { mensaje: 'Los dólares que se devuelven de vuelto no pagan la factura…', que_hacer: 'Ajusta los montos: menos bolívares o menos dólares.' },
      { mensaje: 'La tasa cambió mientras cobrabas', que_hacer: 'No se emitió nada. Pulsa **Resto** para volver a poner el pago y confirma.' },
      { mensaje: 'El saldo a favor escrito no cuadra con lo que se cobra: si otra caja usó el saldo del cliente, o si se escribió de más, vuelve a abrir el cobro', que_hacer: 'Otra caja usó el saldo del mismo cliente, o el saldo escrito pasa de lo que se cobra (con saldo no hay vuelto). No se cobró nada: cierra la ventana de cobro y ábrela otra vez.' },
      { mensaje: 'Esa clave de operación ya se usó en otra operación: vuelve a abrir el cobro', que_hacer: 'El cobro traía los datos de otra operación. Cierra la ventana de cobro y ábrela otra vez.' },
    ],
    manual: '4.7',
    relacionados: ['pos.vuelto', 'pos.zelle', 'pos.banesco', 'pos.credito', 'general'],
    claves: ['cobro', 'cobrar', 'forma de pago', 'pago mixto', 'IGTF', 'tasa BCV', 'tasa 2', 'referencial', 'Resto', 'punto de venta', 'pago móvil', 'transferencia', 'saldo a favor', 'así está cobrando', 'divisas'],
  },
  {
    id: 'pos.vuelto',
    titulo: 'Vuelto y sobrante',
    padre: 'pos',
    resumen: 'Cuando el cliente entrega de más en efectivo, la ventana de cobro muestra el panel **Vuelto**: cuánto sobra y cómo lo entregas.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Entregar el vuelto',
        pasos: [
          'El panel dice «Vuelto: sobran …. ¿Cuánto se entrega?», con los dos campos ya llenos.',
          'Si no tocas nada, el sistema reparte todo: primero en dólares (hasta lo recibido en efectivo $) y el resto en bolívares.',
          'Escribe en **En dólares ($)** o en **En bolívares (Bs)** lo que vas a entregar: el otro campo se completa solo con lo que falta.',
          'En **Los Bs salen por** elige **Efectivo Bs**, **Pago móvil** o **Transferencia** (con su referencia, si ya se hizo).',
          'A la derecha dice **Vuelto completo** o **Falta por devolver …**. Después pulsa **Cobrar**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'En dólares solo se devuelve lo recibido en efectivo $. Debajo del campo dice hasta cuánto.',
          'Debajo del campo de bolívares dice a cuántos dólares equivale.',
          '**Resto en Bs** y **Resto en $** completan en esa moneda lo que falta. **Repartir solo** vuelve a lo que propone el sistema.',
          'Lo que no se devuelve (falta de sencillo) queda como **sobrante por vuelto**: se registra en el cobro y aparece en el cierre de caja.',
          'El vuelto por pago móvil o transferencia no sale del efectivo de la caja.',
          'Solo el efectivo da vuelto: un Zelle o un punto por más del monto no dan vuelto.',
        ],
      },
      { tipo: 'nota', texto: 'Si un Zelle o USDT que la dueña ya cargó es mayor que la venta, el cobro ofrece **Registrar anticipo y pagar con el saldo**. Si no está cargado, regístralo como **Abono** y cobra la venta con el saldo a favor.' },
    ],
    mensajes: [
      { mensaje: 'El excedente … solo puede devolverse como vuelto de efectivo', que_hacer: 'Ajusta el monto. Si es un Zelle o USDT que la dueña ya cargó, usa **Registrar anticipo y pagar con el saldo**; si no, regístralo como abono.' },
      { mensaje: 'Solo se devuelve en dólares lo recibido en efectivo $', que_hacer: 'Da el resto del vuelto en bolívares.' },
      { mensaje: 'El vuelto (…) es más de lo que sobra (…)', que_hacer: 'Baja el vuelto hasta lo que sobra.' },
    ],
    manual: '4.8',
    relacionados: ['pos.cobro', 'cierres'],
    claves: ['vuelto', 'sobrante', 'sencillo', 'cambio de billete', 'devolver efectivo'],
  },
  {
    id: 'pos.banesco',
    titulo: 'Verificar un pago con Banesco',
    padre: 'pos',
    resumen: 'En un pago móvil o una transferencia, **Verificar transacción** consulta en Banesco si el pago entró a la cuenta de la tienda, antes de cobrar.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Verificar el pago',
        pasos: [
          'En la línea de **Pago móvil** o **Transferencia** pulsa **Verificar transacción**.',
          'Elige el **banco** desde donde pagó el cliente y, en un pago móvil, escribe su **celular**.',
          'Escribe la **referencia**: los últimos 6 dígitos. De Banesco a Banesco, la completa o sus últimos 6 (sin fecha). En un pago móvil puedes dejarla vacía si escribes el **monto**.',
          'Revisa la **fecha** (viene la de hoy) y el **monto en Bs** (viene el de la línea). Pulsa **Consultar en Banesco**.',
          'Confirma con el cliente y pulsa **Usar este pago**. La línea queda **Verificado con Banesco**, con el monto y la referencia del banco.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Solo sirve un pago que entró a la cuenta de la tienda y que no se usó en otro cobro. Los demás dicen por qué no.',
          'Un pago del banco paga un solo cobro: al cobrar se comprueba de nuevo, aunque dos cajas lo intenten a la vez.',
          'El mismo pago puede salir con otra referencia según cómo se busque: el sistema lo reconoce por sus **últimos 6 dígitos** y el monto. Si ya se cobró (a mano o verificado) sale «Ya se registró a mano en …» o «Ya se usó en …».',
          'Verificar no es obligatorio: sin verificar se cobra como siempre. **Quitar** vuelve la línea a la verificación manual.',
          'Si una referencia escrita a mano con los mismos últimos 4 dígitos tiene el mismo monto, un aviso te pide confirmarlo con el cliente.',
          'El detalle del documento dice **verificado con Banesco** en esa línea.',
        ],
      },
      { tipo: 'nota', texto: 'Esta verificación es solo para la cuenta **Banesco** de la tienda. Un pago a otra cuenta se verifica a mano.' },
    ],
    mensajes: [
      { mensaje: 'Banesco no encontró ningún pago con esos datos', que_hacer: 'Revisa el banco, el celular, la referencia y la fecha. Si el cliente insiste, verifica a mano.' },
      { mensaje: 'Banesco está en horario de mantenimiento…', que_hacer: 'Verifica el pago a mano y cobra como siempre.' },
      { mensaje: 'Banesco recibió esa misma consulta hace un momento…', que_hacer: 'Espera unos segundos y vuelve a consultar.' },
      { mensaje: 'Banesco no respondió a tiempo…', que_hacer: 'Intenta de nuevo o verifica a mano.' },
      { mensaje: 'La transacción de Banesco … ya se usó en …', que_hacer: 'Ese pago ya pagó otro cobro. Verifícalo con el cliente.' },
      { mensaje: 'Banesco confirmó Bs … El monto escrito (Bs …) no coincide', que_hacer: 'El monto de la línea tiene que ser el que confirmó el banco. Quita la verificación si el cliente pagó otra cosa.' },
      { mensaje: 'La conexión con Banesco aún no está configurada en el servidor', que_hacer: 'Verifica el pago a mano. La activa quien administra el servidor.' },
      { mensaje: 'Hiciste muchas consultas al banco en poco tiempo', que_hacer: 'Espera un minuto.' },
      { mensaje: 'La referencia … es la del pago de Banesco …, ya usado en …', que_hacer: 'Ese pago ya se cobró verificado. Verifícalo con el cliente.' },
    ],
    manual: '4.7',
    relacionados: ['pos.cobro', 'pos.zelle'],
    claves: ['Banesco', 'verificar', 'verificar transacción', 'pago móvil', 'transferencia', 'referencia', 'banco', 'confirmar pago'],
  },
  {
    id: 'pos.zelle',
    titulo: 'Zelle y USDT al cobrar',
    padre: 'pos',
    resumen: 'Al escribir la referencia de un Zelle o un USDT, el sistema la busca entre los pagos que cargó la dueña: muestra quién lo envió y pone el monto.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Al escribir la referencia',
        items: [
          'Si la dueña ya lo cargó, sale «Recibido de …» con el nombre, el monto y la cuenta que lo recibió, y el monto se pone solo. El monto debe coincidir.',
          'Si lo cargó con el otro medio (como Binance y lo estás cobrando como Zelle), sale «está cargado como …: cambia la forma de pago».',
          'Si no está cargado, el aviso dice que el cobro quedará **por verificar**. Se puede cobrar igual: la dueña lo revisa después.',
          'Si la referencia ya se usó, lo dice y no deja repetirla.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Buscar el pago por nombre',
        pasos: [
          'Si el cliente no tiene la referencia, escribe en ese mismo campo al menos 3 letras del nombre de quien envió el pago (o parte de la referencia).',
          'Debajo sale **Pagos recibidos sin usar que coinciden**, con el nombre, el monto, el medio, la cuenta, la referencia y la fecha.',
          'Confirma con el cliente el nombre y el monto y pulsa el suyo: la forma de pago, la referencia y el monto se llenan solos.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'El pago es mayor que la venta',
        pasos: [
          'Un recuadro ámbar dice de cuánto es el pago y cuánto hace falta aquí. Lo que sobra de un Zelle o un USDT no se entrega como vuelto.',
          'Pulsa **Registrar anticipo y pagar con el saldo**. El pago entra completo al saldo a favor del cliente, sin descontarle nada. Si la venta es una factura, su IGTF se cobra en ella.',
          'La línea pasa a **Saldo a favor** por lo que hace falta. Lo que no se use le queda al cliente para la próxima compra.',
          'El recibo del anticipo queda en la ficha del cliente.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Con Consumidor Final no se registra un anticipo: pon al cliente con su cédula o RIF.' },
    ],
    mensajes: [
      { mensaje: '… está cargado como …: cambia la forma de pago', que_hacer: 'Elige la forma de pago que dice el aviso.' },
      { mensaje: '… se recibió $ … de …. El monto escrito ($ …) no coincide', que_hacer: 'Cobra el monto que se recibió.' },
      { mensaje: 'Este pago es de $ … y aquí hacen falta $ …', que_hacer: 'Pulsa **Registrar anticipo y pagar con el saldo**.' },
      { mensaje: 'Consumidor final no acumula saldo a favor…', que_hacer: 'Pon al cliente con su cédula o RIF.' },
      { mensaje: 'Esa referencia ya se usó …', que_hacer: 'Ese pago ya se cobró: verifícalo con el cliente.' },
    ],
    manual: '4.7',
    relacionados: ['pos.cobro', 'pagos-recibidos'],
    claves: ['Zelle', 'USDT', 'Binance', 'referencia', 'por verificar', 'anticipo', 'recibido de', 'buscar pago'],
  },
  {
    id: 'pos.credito',
    titulo: 'Venta a crédito',
    padre: 'pos',
    resumen: 'Para el cliente que paga después. El crédito (límite en $ y plazo en días) lo asigna la dueña en la ficha del cliente.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Emitir a crédito',
        pasos: [
          'Con el cliente elegido, elige **Crédito** en el selector de condición. El botón cambia a **Emitir a crédito**.',
          'Pulsa **Emitir a crédito**. No se abre el cobro: el documento queda pendiente de pago y vence a los días del plazo del cliente.',
          'Si la venta pasa el límite, el sistema la rechaza y dice lo disponible. Para venderla igual, escribe el motivo en el campo **Motivo para vender sobre el límite…** y vuelve a emitir: se pide la autorización de la dueña.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'El selector de condición sale cuando el cliente tiene crédito.',
          'La autorización de crédito solo se pide si la venta de verdad no cabe en el límite. Si escribiste el motivo y la venta cabe, sale como cualquier venta a crédito y no se gasta ninguna autorización.',
          'Un cliente con documentos vencidos no compra ni a crédito ni de contado sin autorización.',
          'El documento se cobra después desde **Documentos**, con **Cobrar saldo**.',
          'Consumidor Final no compra a crédito: ninguna autorización se lo da.',
        ],
      },
      { tipo: 'nota', texto: 'Tú ves el selector con cualquier cliente. A uno sin crédito le puedes vender a crédito escribiendo el motivo: vence con el plazo normal.', permiso: { clave: 'clientes.credito' } },
    ],
    mensajes: [
      { mensaje: 'Excede el límite de crédito: disponible $ … de $ …', que_hacer: 'Cobra de contado, o escribe el motivo y pide la autorización.' },
      { mensaje: 'El cliente no tiene crédito asignado (condición contado)', que_hacer: 'Cobra de contado, o pide a la dueña que le asigne crédito en su ficha.' },
    ],
    manual: '4.9',
    relacionados: ['documentos.cobrar', 'clientes-ficha.credito', 'autorizacion'],
    claves: ['crédito', 'fiado', 'límite', 'plazo', 'emitir a crédito', 'por cobrar', 'vence'],
  },
  {
    id: 'pos.espera',
    titulo: 'Ventas en espera (F9)',
    padre: 'pos',
    resumen: 'Guarda la venta que está en pantalla mientras atiendes a otro cliente. No toma número ni aparta inventario.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Poner en espera',
        pasos: [
          'Con productos en el carrito, pulsa **F9 · Espera** (o la tecla **F9**).',
          'En **Nombre / referencia** escribe algo que la identifique, por ejemplo «Sr. Gómez, camioneta blanca».',
          'Pulsa **Guardar espera**. El carrito queda limpio para la venta siguiente. Si lo pulsas dos veces, o se corta la conexión y lo pulsas otra vez, queda una sola espera.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Retomar o descartar',
        pasos: [
          'Pulsa **Esperas** (el número dice cuántas hay).',
          'En **Facturas en espera** sale cada una con quién la guardó, la hora y cuántas líneas tiene.',
          '**Retomar** la vuelve al carrito con los precios y la tasa de hoy. **Descartar** la elimina.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Una espera dura solo ese día: se descarta al terminar el día o al cerrar tu caja.',
          'Las piezas de vidrio y los productos con detalles no se guardan en la espera: agrégalos otra vez al retomar.',
          'Al cobrarla toma el número que toque en ese momento.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'Esta venta ya se cobró en otra pantalla', que_hacer: 'Ya salió con otro documento, en otra pestaña o en otra caja. No la cobres otra vez: limpia el carrito.' },
      { mensaje: 'Esa venta en espera ya se cobró o se descartó', que_hacer: 'Volviste a guardar una espera que ya se guardó y después se cobró o se descartó. Revisa **Esperas** y el carrito; si hace falta, guárdala de nuevo.' },
    ],
    manual: '4.6',
    claves: ['espera', 'F9', 'retomar', 'guardar venta', 'descartar'],
  },
  {
    id: 'pos.abono',
    titulo: 'Abono o adelanto',
    padre: 'pos',
    resumen: 'El cliente deja dinero a cuenta. Se recibe solo en divisas y entra completo a su saldo a favor, que después sirve como forma de pago. El IGTF no se cobra en el abono: se cobra en la factura que se pague con ese saldo.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Registrar un abono',
        pasos: [
          'Elige al cliente y pulsa **Abono**. Necesitas tu caja abierta.',
          'Escoge **Efectivo $**, **Zelle** o **USDT** y escribe cuánto adelanta. Zelle y USDT llevan su referencia.',
          'Arriba ves **Adelanta en divisa** y lo que **Queda en saldo a favor**: lo mismo, completo. Un Zelle de 123 $ deja 123 $ de saldo.',
          'Pulsa **Registrar abono**. El abono toma su número y el saldo a favor del cliente sube.',
          'En la ventana que se queda en pantalla pulsa **Imprimir recibo** y después **Listo**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'El IGTF del abono',
        items: [
          'El abono no lleva IGTF: no se le descuenta nada.',
          'Cuando ese saldo paga una factura (de contado, o el cobro de una factura a crédito) o una nota de débito, cuenta como un pago en dólares y la factura cobra su IGTF ese día.',
          'Si ese saldo paga una nota de entrega, no hay IGTF.',
          'El abono no entra al libro de ventas ni al reporte de IGTF: entra la factura que se paga con él, el día en que se emite o se cobra.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'El recibo de abono sale en media carta: lo recibido por forma de pago, lo abonado y el saldo a favor que le queda. El recibo de un abono anterior al 19/10/2026 trae además el IGTF que se le descontó entonces.',
          'Si no lo imprimiste en el momento, se reimprime desde la ficha del cliente. Desde la segunda impresión dice **REIMPRESIÓN**.',
          'En el recibo, **Enviar por correo** se lo manda al cliente a su correo registrado. También desde **Pagos recibidos** de su ficha.',
          'Un Zelle o Binance que la dueña ya cargó también sirve para un abono.',
          'Cuando un Zelle o USDT que la dueña ya cargó es mayor que la venta, el cobro ofrece **Registrar anticipo y pagar con el saldo**: hay que pulsarlo. Si no está cargado, el botón no sale: regístralo aquí como abono.',
          'Consumidor Final no puede dejar abonos.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'Los abonos se reciben solo en efectivo $, Zelle o USDT', que_hacer: 'Recibe el abono en divisas.' },
      { mensaje: 'Indica cuánto adelanta el cliente', que_hacer: 'Escribe el monto que entrega.' },
      { mensaje: 'Consumidor final no acumula saldo a favor', que_hacer: 'Elige al cliente con su cédula o RIF.' },
    ],
    manual: '4.10',
    relacionados: ['clientes-ficha', 'impresion', 'correo', 'pos.cobro'],
    claves: ['abono', 'adelanto', 'anticipo', 'saldo a favor', 'recibo de abono', 'IGTF del abono'],
  },

  // ─── Documentos ─────────────────────────────────────────────────────────────
  {
    id: 'documentos',
    titulo: 'Documentos',
    rutas: ['/documentos', '/documentos/:id'],
    permiso: { clave: 'ventas.emitir' },
    resumen: 'El historial de facturas, notas, notas de crédito, notas de débito y cambios de la sucursal. Un documento emitido no se edita ni se borra.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Buscar',
        items: [
          'Busca por número, control, cliente o RIF.',
          'Filtra por fechas (desde, hasta y **Hoy**) y con cuatro desplegables, cada uno con su nombre encima: **Tipo**, **Pago** (**Pendiente**, **Parcial**, **Pagado**), **Entrega** (**Por entregar**, **Parcial**, **Entregado**) y **Estado** (**Emitidos y anulados**, **Solo emitidos**, **Solo anulados**).',
          'Cada fila dice el documento y su control, el cliente, la fecha, el total, el pago (con la marca **crédito**), la entrega y el cajero.',
          'Un número anulado sale con la marca **ANULADA**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'El detalle',
        items: [
          'Clic en una fila abre el detalle: el cliente y la tasa BCV congelada del documento, los renglones con lo entregado de cada uno, los totales, los pagos y las retenciones.',
          'Arriba, las marcas del pago y de la entrega, **Crédito · vence …**, la retención con su nombre («Retención: base + 25 % del IVA» o «Retención: pago completo (reintegro después)»), **Con devolución** y el documento de origen.',
          'Cada retención dice su estado como en Retenciones: «por recibir», «por cargar», «recibida», «sin efecto»…',
          'Un renglón vendido contra depósito dice de dónde sale; uno con descuento programado dice cuál; un servicio dice «no aplica» en la entrega.',
          'En **Pagos**: cada forma de pago con su referencia, el nombre de quien envió un Zelle, el vuelto, el sobrante por vuelto y el IGTF.',
          'Si se pagó con saldo a favor, debajo dice de qué abono (o nota de crédito) salió, con su recibo y su forma de pago. Se reparte en orden de llegada y es solo informativo: no sale en el papel.',
          'Un cobro por Zelle o USDT que no estaba cargado sale marcado **por verificar**.',
          '**Archivos del documento**, al pie: los soportes que se le adjuntaron a mano (foto o PDF). Quien vende puede adjuntar.',
          '**Correos**, al pie: cada correo que se le envió al cliente de esta venta (el documento, el estado de su retiro, el reintegro), con la fecha, a quién, quién lo envió o **Automático**, si salió y qué decía.',
        ],
      },
      { tipo: 'nota', texto: 'En el detalle ves además la tasa 2 (paralela) congelada del documento y la tasa de cada pago en bolívares. Los demás usuarios ven solo la BCV.', permiso: VE_TASA_2 },
      {
        tipo: 'lista',
        titulo: 'Botones',
        items: [
          '**Imprimir** la primera vez y **Reimprimir** después.',
          '**Enviar por correo** le manda el documento al cliente, a su correo registrado. No sale en un documento anulado.',
          'En una factura o nota con mercancía: **Enviar estado de retiro** le manda lo comprado, lo retirado y lo pendiente (sin precios), y **Copiar mensaje para WhatsApp** copia ese mismo texto.',
          '**Cobrar saldo $ …** cuando el documento tiene saldo pendiente.',
          '**Hoja dañada: anular y reemitir**, solo en facturas, notas de crédito y notas de débito del mismo día.',
          '**Anular hoja en blanco**, arriba en la lista, para una hoja del talonario que se dañó sin imprimir.',
          'Con permiso solo de consulta se ven la lista y el detalle, sin cobrar ni anular.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Pasado el día de la emisión, una venta no se anula aquí: se corrige con una nota de crédito en **Devoluciones**.' },
    ],
    mensajes: [
      { mensaje: '… es de otro día: ya no se reemite', que_hacer: 'Anula la venta con una nota de crédito en **Devoluciones**.' },
      { mensaje: 'Este documento ya se reemitió en otra pantalla', que_hacer: 'Actualiza la pantalla. Si la hoja nueva también se dañó, reemítelo desde ahí.' },
    ],
    manual: '5',
    relacionados: ['devoluciones', 'impresion', 'pos', 'autorizacion', 'correo', 'adjuntos'],
    claves: ['factura', 'nota', 'historial', 'buscar documento', 'reimprimir', 'anulada', 'número de control', 'nota de crédito', 'nota de débito', 'cambio', 'enviar por correo', 'archivos'],
  },
  {
    id: 'documentos.cobrar',
    titulo: 'Cobrar un documento a crédito',
    padre: 'documentos',
    permiso: { clave: 'pagos.cobrar' },
    resumen: 'El saldo de una factura o nota a crédito, o de una nota de débito, se cobra desde su detalle. Admite pago parcial.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cobrar el saldo',
        pasos: [
          'Abre el documento y pulsa **Cobrar saldo $ …**. Necesitas tu caja abierta.',
          'Se abre la ventana de cobro con lo que falta. Puedes cobrar una parte.',
          'Si el cliente tiene **saldo a favor**, sirve como forma de pago: al elegirlo, el monto se pone solo, hasta cubrir lo que falta.',
          'Pulsa **Cobrar**. Sale «Pago registrado» con el saldo que queda.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Cuánto se cobra',
        items: [
          'La ventana dice lo que falta en bolívares y en dólares, con los montos de hoy: cobra lo que dice.',
          'Si una factura o una nota de débito se paga en divisas, se cobra el IGTF de ese pago.',
          'Si se paga con saldo a favor que entró con abonos en divisas, esa parte también lleva su IGTF, como un pago en dólares (también la que volvió de devolver una nota pagada en divisas). El saldo de la devolución de una factura o de un reintegro no.',
        ],
        permiso: SIN_TASA_2,
      },
      {
        tipo: 'lista',
        titulo: 'A qué tasa se cobra',
        items: [
          'Una nota a crédito se cobra a la tasa 2 del día del pago.',
          'Una factura, a los bolívares del papel llevados a la tasa BCV del día del pago. Si se paga el mismo día, coincide con el papel.',
          'Si una factura o una nota de débito se paga en divisas, se cobra el IGTF de ese pago.',
          'Si se paga con saldo a favor que entró con abonos en divisas, esa parte también lleva su IGTF, como un pago en dólares (también la que volvió de devolver una nota pagada en divisas). El saldo de la devolución de una factura o de un reintegro no.',
          'Esta explicación solo la ves tú: los demás ven lo que falta en bolívares y en dólares.',
        ],
        permiso: VE_TASA_2,
      },
      { tipo: 'nota', texto: 'Si al cobrar sale un error de conexión, pulsa **Cobrar** otra vez: el pago no se registra dos veces.' },
      { tipo: 'nota', texto: 'Una caja **Solo facturas** no cobra el saldo de una nota: cóbrala en otra caja.' },
    ],
    mensajes: [
      { mensaje: '… es una caja solo fiscal: no cobra notas de entrega; cóbrala en una caja completa', que_hacer: 'Cobra la nota en otra caja.' },
      { mensaje: 'Debes abrir tu sesión de caja antes de vender o cobrar', que_hacer: 'Abre tu caja en **Caja**.' },
      { mensaje: 'El saldo a favor escrito no cuadra con lo que se cobra: si otra caja usó el saldo del cliente, o si se escribió de más, vuelve a abrir el cobro', que_hacer: 'Otra caja usó el saldo del mismo cliente, o el saldo escrito pasa de lo que se cobra (con saldo no hay vuelto). No se cobró nada: cierra la ventana de cobro y ábrela otra vez.' },
    ],
    manual: '5.2',
    relacionados: ['pos.cobro', 'pos.credito'],
    claves: ['cobrar saldo', 'crédito', 'cuenta por cobrar', 'pago parcial', 'saldo pendiente'],
  },
  {
    id: 'documentos.imprimir',
    titulo: 'Imprimir y reimprimir',
    padre: 'documentos',
    resumen: 'El botón dice **Imprimir** la primera vez y **Reimprimir** después. La reimpresión sale igual que el original, sin marca de copia, y queda en auditoría.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Factura, nota de crédito y nota de débito',
        items: [
          'Salen sobre el papel preimpreso de la imprenta. La pantalla de impresión dice qué hoja colocar: «Coloca la hoja con el N° de control …». Ese aviso no sale en el papel.',
          'En el cuadro de impresión: márgenes «Ninguno», escala 100 %, sin encabezados ni pies.',
          'La factura sale solo en bolívares.',
          'Debajo de **Cond. de Pago** sale la **Hora Emisión**: la hora del sistema en que se emitió. Al reimprimir sale la misma hora.',
          'El nombre del cliente usa todo el primer renglón, hasta el número de la factura pegado al margen derecho; si es muy largo, la letra se achica antes de cortarse. El domicilio tiene dos renglones y también se achica si no cabe. La cabecera ocupa siempre 4 renglones: los márgenes, los productos y los totales no se mueven.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Al reimprimir una factura',
        items: [
          'La pantalla avisa en rojo **REIMPRESIÓN** y dice qué hoja le corresponde: la del N° de control del aviso.',
          'Si la hoja no llegó a salir (cancelaste el cuadro o la impresora no imprimió), imprímela ahora en esa misma hoja.',
          'Si la hoja ya salió, imprime en hoja en blanco o sobre la copia de esa misma hoja.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Si la hoja ya salió, no uses otra hoja preimpresa: gastarías un número de control que el sistema no conoce. Si la hoja original se dañó, usa **Hoja dañada: anular y reemitir**.' },
      { tipo: 'nota', texto: 'La nota de entrega y el Cambio salen en hoja en blanco, con el membrete de los dos logos (las dos tiendas con su fijo y su WhatsApp) y **DOCUMENTO NO FISCAL**. Traen la **Hora Emisión** debajo de la condición de pago. La nota ya no trae la línea de pagos al pie; el Cambio sigue diciendo con qué se pagó la diferencia.' },
      { tipo: 'nota', texto: 'Un número anulado por hoja dañada no se imprime: su hoja dice «… está ANULADA por hoja dañada: no se imprime. La venta sigue en …». Abrirla no cuenta como impresión.' },
    ],
    mensajes: [{ mensaje: '… está ANULADA por hoja dañada: no se imprime. La venta sigue en …', que_hacer: 'Imprime el documento que nombra el mensaje.' }],
    manual: '5.3',
    relacionados: ['impresion', 'documentos.reemitir', 'correo'],
    claves: ['imprimir', 'reimprimir', 'copia', 'papel preimpreso', 'hoja', 'número de control', 'reimpresión', 'hora de emisión'],
  },
  {
    id: 'documentos.reemitir',
    titulo: 'Hoja dañada: anular y reemitir',
    padre: 'documentos',
    permiso: { clave: 'ventas.emitir', nivel: 'total' },
    resumen: 'Para cuando la hoja de una factura, nota de crédito o nota de débito se dañó o salió mal impresa. El número y el control viejos quedan anulados y la misma venta toma los siguientes.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Anular y reemitir',
        pasos: [
          'Abre el documento y pulsa **Hoja dañada: anular y reemitir**.',
          'En **¿Qué pasó con la hoja?** escribe lo que pasó (al menos 5 letras) y pulsa **Anular y reemitir**. Si tu usuario no tiene el permiso, se pide autorización.',
          'Se abre una ventana con el número y el control nuevos. Coloca la hoja siguiente en la impresora y pulsa **Imprimir**; después, **Listo**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Cuándo se puede',
        items: [
          'Solo el mismo día de la emisión.',
          'Con la caja de ese documento abierta y sin conteo declarado.',
          'Si el documento no tiene nota de crédito, nota de débito ni cambio.',
          'Una nota de entrega o un cambio no se anulan: se reimprimen.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pasa',
        items: [
          'La misma venta, con sus renglones, cobros y entregas, toma el número y el control siguientes.',
          'El número anulado sigue en la lista con la marca **ANULADA** y un enlace a la venta. No se imprime ni se cobra.',
          'La hoja nueva empieza su cuenta de impresiones en cero: su primera impresión no sale como reimpresión. Las impresiones de la hoja vieja quedan con el número anulado y en la auditoría.',
          'En el cierre de caja sale como anulada y en el libro de ventas sale en cero.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'No se deshace: el número y el control viejos quedan anulados. Pasado el día, la venta se anula con una nota de crédito en **Devoluciones**.' },
    ],
    mensajes: [
      { mensaje: '… es de otro día: ya no se reemite', que_hacer: 'Anula la venta con una nota de crédito en **Devoluciones**.' },
      { mensaje: 'Este documento ya se reemitió en otra pantalla', que_hacer: 'Otra persona ya lo hizo. Actualiza la pantalla; si la hoja nueva también se dañó, reemítelo desde ahí.' },
      { mensaje: 'La caja de … ya cerró: no se reemite después del cierre', que_hacer: 'Corrige la venta con una nota de crédito en **Devoluciones**.' },
      { mensaje: 'La caja de … ya declaró su conteo: no se reemite hasta terminar el cierre', que_hacer: 'Con el conteo declarado la caja no reemite. Después del cierre la venta se corrige con una nota de crédito.' },
      { mensaje: 'Esa clave de operación ya se usó para reemitir otro documento: vuelve a abrir «Hoja dañada» y registra de nuevo', que_hacer: 'Cierra la ventana, abre otra vez **Hoja dañada: anular y reemitir** en este documento y registra.' },
    ],
    manual: '5.4',
    relacionados: ['autorizacion', 'documentos.imprimir'],
    claves: ['hoja dañada', 'anular', 'reemitir', 'atasco', 'mal impresa', 'número de control'],
  },
  {
    id: 'documentos.otro-nombre',
    titulo: 'Facturar a otro nombre',
    padre: 'documentos',
    permiso: { clave: 'devoluciones.registrar' },
    resumen: 'Para cuando el cliente compró a su nombre y después pide la factura a nombre de otra persona o de su empresa. La factura se revierte con una nota de crédito por el total y sale una factura nueva al otro cliente, con la misma información y pagada con ese crédito.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Pasarla a otro nombre',
        pasos: [
          'Abre la factura (o la nota de entrega) y pulsa **Facturar a otro nombre** (en la nota, **Pasar a otro nombre**).',
          'Busca al cliente por nombre, RIF o cédula y elígelo. Si no está, pulsa **Cliente nuevo** y regístralo con su RIF y su dirección fiscal.',
          'Revisa el resumen: la nota de crédito por el total y la factura nueva con los mismos montos.',
          'Escribe el **Motivo** y pulsa **Emitir nota de crédito y factura nueva**. Si tu usuario no tiene el permiso, se pide autorización (con la clave de quien la da o a distancia).',
          'En la ventana que queda abierta imprime en orden, cada una en su hoja preimpresa: **1 · Imprimir nota de crédito** y después **2 · Imprimir factura nueva**. Pulsa **Listo**: se abre la factura nueva.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pasa',
        items: [
          'La nota de crédito copia la factura original: los mismos renglones, montos en $ y en Bs y tasas. Sale con el motivo «Cambio de nombre del comprador».',
          'La factura nueva lleva los mismos productos, cantidades, precios, descuentos, combos y tasas de la original, con la fecha de hoy.',
          'Queda pagada con el crédito de la nota de crédito: no se cobra otra vez, no se repite la referencia del pago y no lleva IGTF (el de la original queda como estaba).',
          'Sus **Observaciones** dicen a cuál sustituye, con qué nota de crédito se pagó y cómo se pagó la original («transferencia ref. … del …»). Se ven en la pantalla del documento; no salen en el papel.',
          'No mueve inventario: lo entregado sigue entregado y lo que faltaba por entregar se retira con la factura nueva, desde **Entregas**.',
          'En el libro de ventas salen la original, su nota de crédito y la nueva, todas en su día. La nota de crédito y la nueva se anulan entre sí.',
          'Si el cliente nuevo es agente de retención, la factura nueva va como «pago completo»: la retención queda **por cargar** en **Retenciones** y, cuando traiga el comprobante, se le reintegra lo retenido como cualquier pago completo.',
          'En la nota de entrega es igual, con un **Cambio** en lugar de la nota de crédito y una nota nueva.',
          'La dueña lo hace con su permiso; puede dárselo al administrador. Si lo hace otro (con el permiso o con una autorización), ella recibe el aviso con lo que de verdad se hizo.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Cuándo no se puede',
        items: [
          'Si la factura todavía debe algo: primero se cobra.',
          'Si ya tiene una nota de crédito, una nota de débito o un cambio, o ya se pasó a otro nombre.',
          'Si se cobró «base + 25 %» o ya tiene cargado un comprobante de retención.',
          'Si tiene piezas de vidrio o productos con detalle.',
          'Al mismo cliente, a Consumidor Final o a un cliente sin dirección fiscal (para la factura).',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'No se deshace: la nota de crédito y la factura nueva quedan emitidas con sus números y sus controles. Se puede hacer cualquier día.' },
    ],
    mensajes: [
      { mensaje: '… tiene $ … por cobrar: cóbrala antes de pasarla a otro nombre', que_hacer: 'Cobra el saldo con **Cobrar saldo** y vuelve a intentarlo.' },
      { mensaje: '… ya tiene NC …: el cambio de nombre se hace sobre la factura completa', que_hacer: 'Con una nota de crédito anterior no se hace. Consulta con la administración.' },
      { mensaje: '… ya se pasó a otro nombre', que_hacer: 'Abre la factura nueva desde el aviso del documento: los cambios siguientes se hacen sobre ella.' },
      { mensaje: 'Para la factura a nombre de … hace falta su RIF y su dirección fiscal', que_hacer: 'Completa la dirección fiscal en la ficha del cliente y vuelve a intentarlo.' },
    ],
    manual: '5.6',
    relacionados: ['autorizacion', 'documentos.imprimir', 'devoluciones', 'clientes-ficha.saldo'],
    claves: ['otro nombre', 'cambio de nombre', 'factura a nombre de la empresa', 'RIF', 'nota de crédito', 'rehacer factura'],
  },
  {
    id: 'documentos.hoja-blanco',
    titulo: 'Anular una hoja en blanco',
    padre: 'documentos',
    permiso: { clave: 'ventas.emitir', nivel: 'total' },
    resumen: 'Para cuando una hoja del talonario fiscal se daña sin haber impreso nada (atasco, mancha, corte de luz). Se pierde solo ese número de control.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Anular la hoja',
        pasos: [
          'En **Documentos**, pulsa **Anular hoja en blanco**.',
          'En **¿Qué pasó con la hoja?** escribe lo que pasó (al menos 5 letras).',
          'Pulsa **Anular el control**. Pide el mismo permiso o autorización que anular y reemitir.',
          'Guarda la hoja dañada con el talonario.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pasa',
        items: [
          'Se anula el número de control que le toca a la sucursal. La próxima factura toma el siguiente.',
          'La lista **Hojas anuladas** muestra cada control con su motivo, la fecha y quién lo anuló.',
          'Si pulsas **Anular el control** dos veces, o se corta la conexión y lo pulsas otra vez, se anula una sola hoja.',
          'La hoja sale en el cierre de tu caja, si la tienes abierta, aunque no hayas emitido documentos. Si no tienes caja abierta, sale en el cierre de sucursal del día como «Hojas anuladas fuera de caja».',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Si la hoja tenía una factura impresa, no uses esto: anúlala desde el detalle de esa factura con **Hoja dañada: anular y reemitir**.' },
    ],
    manual: '5.5',
    relacionados: ['documentos.reemitir', 'autorizacion'],
    claves: ['hoja en blanco', 'talonario', 'control', 'atasco', 'mancha', 'anular control'],
  },

  // ─── Cotizaciones ───────────────────────────────────────────────────────────
  {
    id: 'cotizaciones',
    titulo: 'Cotizaciones',
    rutas: ['/cotizaciones', '/cotizaciones/:id'],
    permiso: { clave: 'cotizaciones.crear' },
    resumen: 'Presupuestos para el cliente, con precios y tasas congelados al guardar. Se re-tasan al día y pasan directo al punto de venta.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'La lista',
        items: [
          'Busca por número, cliente o RIF. Filtra por estado: **Vigentes**, **Vencidas**, **Facturadas** o **Anuladas**.',
          'Cada fila dice el número, el cliente, la versión, el total en $ y en Bs, el vencimiento (**Sin vencimiento** si no vence), el estado, el documento con que se facturó y quién la creó.',
          'Clic en una fila abre la cotización. **Nueva cotización** abre el editor.',
        ],
      },
      { tipo: 'nota', texto: 'La lista trae además la columna **Tasa 2**, y la cotización dice la tasa 2 congelada y, si las tasas cambiaron, la cotizada y la de hoy. Solo tú lo ves: los demás ven la BCV y el aviso «Las tasas cambiaron desde que se cotizó».', permiso: VE_TASA_2 },
      {
        tipo: 'lista',
        titulo: 'La cotización',
        items: [
          'Arriba, el estado, la vigencia y la tasa BCV congelada. Si se facturó, el enlace al documento.',
          '**Enviar por correo** se la manda al cliente, a su correo registrado, con el mismo formato del papel. No sale en una anulada.',
          'Cada renglón con sus tres precios (**Bs · $ a BCV · $**). Un producto inactivo sale en rojo: hay que quitarlo o sustituirlo.',
          '**Totales**: **Total sin IVA**, el IVA y el **Total con IVA**, a la tasa congelada.',
          '**Historial de versiones** muestra cada versión con su motivo, la tasa, el total, la fecha y quién la hizo.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Re-tasar',
        pasos: [
          'Si las tasas cambiaron o la cotización venció, sale un aviso con **Re-tasar ahora**.',
          'Pulsa **Re-tasar** (o **Re-tasar ahora**): toma los precios y las tasas de hoy y guarda una versión nueva.',
          'Si nada cambió, no crea versión y lo dice.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Pasar al POS',
        pasos: [
          'Pulsa **Pasar al POS**. Los renglones van al carrito al precio y la tasa de hoy, y el aviso dice las diferencias.',
          'Si la cotización era de Consumidor Final, elige al cliente antes de cobrar.',
          'Cobra como siempre. Al emitir, la cotización queda **Facturada** con el enlace al documento.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Si la cotización tiene descuento',
        items: [
          'Pasa al punto de venta con su descuento: la franja de arriba lo dice.',
          'Si quien cobra puede dar descuentos, se factura así.',
          'Si no puede, el carrito avisa cuánto es el descuento y que se pedirá autorización al cobrar.',
          'Para facturar sin el descuento, pulsa **Quitar el descuento y facturar a precio completo**.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Anular',
        pasos: ['Pulsa **Anular**.', 'Escribe el **Motivo** y pulsa **Anular**.', 'Queda en el historial como anulada y ya no pasa al punto de venta.'],
      },
      {
        tipo: 'lista',
        titulo: 'Imprimir',
        items: [
          '**Imprimir / PDF** abre la hoja con los dos logos y las dos tiendas con su fijo y su WhatsApp: precios en Bs por renglón, base, IVA y total en **Bs** y **$ a tasa BCV**, y el referencial para pagos en divisas.',
          'Dice «Válida hasta» solo si la cotización vence. Al pie van las leyendas que la dueña edita en **Configuración**.',
          'Si lleva descuento, sale la columna **% Dscto** y los totales con **Sub Total**, **Descuento** y **Base imponible**.',
        ],
      },
      { tipo: 'nota', texto: 'Una cotización facturada o anulada ya no se edita, no se re-tasa y no pasa al punto de venta.' },
    ],
    mensajes: [
      { mensaje: 'La cotización … trae descuento … Tu usuario no puede dar descuentos', que_hacer: 'En el punto de venta: pide la autorización al cobrar, o pulsa **Quitar el descuento y facturar a precio completo**.' },
      { mensaje: 'La cotización … ya fue facturada (…) y no admite cambios', que_hacer: 'Ábrela para ver el documento con que se facturó.' },
      { mensaje: 'La lista de precios «…» de … está desactivada: cámbiale la lista en su ficha (Lista y crédito) antes de cotizarle', que_hacer: 'De una lista desactivada no sale ningún precio: quien administra el crédito le elige otra lista en la ficha del cliente.' },
    ],
    manual: '6',
    relacionados: ['cotizaciones-editor', 'pos', 'clientes', 'correo'],
    claves: ['cotización', 'presupuesto', 're-tasar', 'versiones', 'vencida', 'pasar al POS', 'vigencia', 'PDF', 'enviar por correo'],
  },
  {
    id: 'cotizaciones-editor',
    titulo: 'Nueva cotización y editar',
    rutas: ['/cotizaciones/nueva', '/cotizaciones/:id/editar'],
    permiso: { clave: 'cotizaciones.crear' },
    resumen: 'Arma la cotización: cliente, productos, vigencia y observaciones. Precios y tasa se congelan al guardar; cada guardado crea una versión.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Hacer una cotización',
        pasos: [
          'Pulsa **Nueva cotización**. Arranca con Consumidor Final. Para otro cliente, haz clic en el campo del cliente y elígelo: los precios dependen de su lista.',
          'Busca el producto por código, nombre o marca. **Enter** agrega el primero, o pulsa la presentación que quieras (cada una dice su precio en dólares). Si hay más resultados de los que caben, abajo dice cuántos son y con **Anterior** y **Siguiente** se ven los demás.',
          'Cambia la cantidad en **Cant.** Cada renglón muestra sus tres precios: **Bs · $ a BCV · $**.',
          'Si quieres, escribe la **Vigencia (días · 0 = sin vencer)** y las **Observaciones**.',
          'Revisa **Sin IVA** y **Total con IVA** y pulsa **Guardar cotización**. Recibe su número. Si la pulsas dos veces, o se corta la conexión y la pulsas otra vez, sale una sola cotización.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Editar',
        pasos: ['En la cotización pulsa **Editar**.', 'Cambia el cliente, los renglones o las cantidades.', 'Pulsa **Guardar nueva versión**.'],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Vigencia en blanco o en 0: la cotización no vence.',
          'Los descuentos programados se aplican solos.',
          '**Combo o receta** funciona como en el punto de venta: el combo entra como bloque con su descuento (sin el permiso de descuentos) y pasa así al punto de venta. Desarmarlo o quitar uno de sus productos le quita el descuento.',
          'Consumidor Final se usa solo aquí, para cotizar.',
          'Un renglón en rojo (inactivo o sin precio) no deja guardar: quítalo o cámbialo.',
          '**Cancelar** sale sin guardar.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Descuento',
        items: ['El campo **Descuento %** es para toda la cotización.', 'El descuento sale en el papel y pasa con la cotización al punto de venta.'],
        permiso: { clave: 'ventas.descuento' },
      },
      { tipo: 'nota', texto: 'Sin el permiso de descuentos no se cotiza con descuento. Si la cotización ya trae uno, se ve y se conserva al guardar: no se puede subir ni aumentar la cantidad de un renglón con descuento, pero sí quitarlo con **Quitarlo** (o **quitar** en el renglón).' },
    ],
    mensajes: [
      { mensaje: 'Tu usuario no puede dar descuentos: guarda la cotización sin descuento o pídesela a quien tenga el permiso', que_hacer: 'Que la cotice quien tiene el permiso de descuentos.' },
      { mensaje: '… se vende en múltiplos de …', que_hacer: 'Corrige la cantidad.' },
      { mensaje: '… está inactivo: quítalo o sustitúyelo', que_hacer: 'Quita ese renglón o cámbialo por otro producto.' },
      { mensaje: '… no tiene precio en la lista del cliente', que_hacer: 'Quita el renglón, o pide a la dueña que le ponga precio.' },
    ],
    manual: '6.1',
    relacionados: ['cotizaciones', 'combos.agregar'],
    claves: ['nueva cotización', 'editar cotización', 'presupuesto', 'vigencia', 'observaciones', 'versión', 'descuento'],
  },

  // ─── Clientes ───────────────────────────────────────────────────────────────
  {
    id: 'clientes',
    titulo: 'Clientes',
    rutas: ['/clientes'],
    permiso: { clave: 'clientes.crear' },
    resumen: 'Todos los clientes con su lista de precios, su clasificación, su crédito y su saldo a favor. Toda venta va a un cliente identificado.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'La lista',
        items: [
          'Busca por nombre, RIF o teléfono.',
          'Filtra con **Contribuyente** (**Ordinarios** o **Especiales**) y con **Condición** (**Contado** o **Crédito**). Marca **Incluir inactivos** para verlos también.',
          'Cada fila dice el RIF, el nombre con su teléfono y correo, la lista, la clasificación, el límite de crédito y el saldo a favor.',
          'Clic en una fila abre la ficha.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Botones',
        items: ['**Nuevo cliente** registra uno.', '**Cargar Excel** carga muchos de una vez.'],
        permiso: { clave: 'clientes.crear', nivel: 'total' },
      },
      { tipo: 'nota', texto: 'Una persona con su cédula y con su RIF es un solo cliente: si ya está con la cédula, no entra otra vez con el RIF, ni al revés.' },
      { tipo: 'nota', texto: 'Con permiso solo de consulta se ven la lista y las fichas, sin botones para cambiarlas.' },
    ],
    mensajes: [{ mensaje: 'Ya existe un cliente con RIF …: …', que_hacer: 'Pulsa **Ver ficha** en el aviso y usa el que ya existe.' }],
    manual: '7',
    relacionados: ['clientes-ficha', 'pos'],
    claves: ['cliente', 'RIF', 'cédula', 'buscar cliente', 'contribuyente especial', 'inactivo'],
  },
  {
    id: 'clientes.nuevo',
    titulo: 'Nuevo cliente y editar datos',
    padre: 'clientes',
    resumen: 'El formulario para registrar un cliente (en **Clientes** o con **Nuevo** en el punto de venta) y para cambiar sus datos básicos desde la ficha.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Registrar un cliente',
        pasos: [
          'Pulsa **Nuevo cliente** (o **Nuevo** en el punto de venta).',
          'En **RIF / Cédula** elige la letra (V, E, J, G, P, C) y escribe solo números, sin guiones ni puntos.',
          'Escribe el **Nombre / Razón social**.',
          'Escribe la **Dirección fiscal**: hace falta para facturarle.',
          'Escribe el **Teléfono (obligatorio)**: sin teléfono no se guarda.',
          'Si es el caso, elige el **Tipo de contribuyente** (Ordinario o Especial) y marca **Contribuyente municipal**.',
          '**Correo** y **Notas** son opcionales. Pulsa **Crear cliente**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Cómo se escribe la identificación',
        items: [
          'Persona con cédula: la cédula tal cual, sin dígito final. Queda como V-18222333.',
          'Persona con RIF: el RIF con su dígito final. Queda como V-18222333-4.',
          'Empresa (J, G, C) o pasaporte (P): siempre el RIF completo, con su dígito final. Queda como J-12345678-9.',
          'Si pegas el RIF completo con guiones, la letra pasa sola al desplegable.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Al crearlo',
        items: [
          'Nace con la lista normal (Detal) y de contado. La lista y el crédito los cambia la dueña.',
          'Si lo marca como especial o municipal alguien que no administra la ficha fiscal, la administración recibe el aviso para confirmarlo.',
          'Creado desde el punto de venta, queda elegido y el carrito sigue como estaba.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Editar datos',
        items: [
          'En la ficha, **Editar datos** cambia el RIF o la cédula, el nombre, la dirección, el teléfono, el correo y las notas. Pulsa **Guardar**.',
          'El teléfono no se puede dejar vacío.',
          'La clasificación fiscal no se cambia aquí: va en **Lista y crédito**.',
          'La ficha de Consumidor Final no se edita.',
        ],
      },
      { tipo: 'nota', texto: 'Con el permiso de crédito, al marcar **Contribuyente municipal** aparece **% retención municipal**. Vacío usa el % general de **Configuración**.', permiso: { clave: 'clientes.credito' } },
    ],
    mensajes: [
      { mensaje: 'RIF o cédula inválidos: elige la letra y escribe solo los números', que_hacer: 'Letra en el desplegable y solo números: la cédula sin dígito final, el RIF con su dígito final.' },
      { mensaje: 'Ya existe un cliente con RIF …: …', que_hacer: 'Pulsa **Ver ficha** en el aviso y usa el que ya existe.' },
      { mensaje: 'Indica un teléfono de contacto (mínimo 7 dígitos)', que_hacer: 'Escribe el teléfono: la nota de entrega lo pide.' },
    ],
    manual: '7.1',
    relacionados: ['clientes-ficha', 'pos'],
    claves: ['nuevo cliente', 'registrar cliente', 'RIF', 'cédula', 'dígito', 'teléfono', 'dirección fiscal', 'editar cliente'],
  },
  {
    id: 'clientes.excel',
    titulo: 'Cargar clientes desde Excel',
    padre: 'clientes',
    permiso: { clave: 'clientes.crear', nivel: 'total' },
    resumen: 'Carga muchos clientes de una vez con la plantilla del sistema. Primero revisa el archivo y no carga nada; solo entran al confirmar.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cargar',
        pasos: [
          'Pulsa **Cargar Excel**.',
          'Pulsa **Descargar plantilla**. Trae los títulos, dos filas de ejemplo y una hoja de instrucciones.',
          'Borra las filas de ejemplo y escribe un cliente por fila. No cambies los títulos de la primera fila.',
          'Pulsa **Elegir archivo** y sube el Excel. Sale cuántas filas se crean, se actualizan, se dejan igual y tienen error, con el motivo de cada error (los errores primero).',
          'Para cambiar los que ya existen, marca **Si ya existe (mismo RIF), actualizarlo con lo del archivo**. La revisión se rehace.',
          'Pulsa **Cargar … clientes**. Una carga grande puede tardar un minuto: no cierres la ventana.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Las columnas',
        items: [
          'RIF o cédula, Nombre o razón social, Teléfono, Dirección fiscal, Correo y Notas.',
          'RIF o cédula, nombre y teléfono son obligatorios. El RIF va con su letra y su dígito final (J-12345678-9); la cédula, con su letra y sin dígito final (V-12345678).',
          'Todos entran de contado, con la lista normal y como contribuyentes ordinarios. El crédito, la lista y la clasificación los pone la dueña en la ficha.',
          'Los que ya existen se dejan igual. Con la casilla marcada cambian nombre, teléfono, dirección, correo y notas; un dato vacío no borra el que había.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Si hay errores',
        items: [
          'Las filas con error no entran; las demás sí.',
          'Si alguna falla al cargar, la ventana queda abierta con **Carga hecha.** y el resultado de cada fila.',
          'Corrige esas filas en el Excel y súbelo otra vez: las que ya entraron no se duplican. Pulsa **Cerrar** al terminar.',
        ],
      },
      { tipo: 'nota', texto: 'Solo archivos .xlsx, de hasta 2 MB y 1000 filas. Se lee la primera hoja.' },
    ],
    mensajes: [
      { mensaje: 'El archivo no es un Excel (.xlsx)', que_hacer: 'Usa la plantilla del sistema.' },
      { mensaje: 'Al Excel le faltan las columnas …', que_hacer: 'Descarga la plantilla y llena esa.' },
      { mensaje: 'El Excel tiene … filas; el máximo por carga es 1000', que_hacer: 'Divídelo en varios archivos.' },
      { mensaje: 'El archivo pesa más de 2 MB', que_hacer: 'Divídelo en varios archivos.' },
      { mensaje: 'Ya existe un cliente con esa cédula: …', que_hacer: 'Quita esa fila: el cliente ya está.' },
    ],
    manual: '29.5',
    claves: ['Excel', 'cargar clientes', 'plantilla', 'importar', 'xlsx'],
  },
  {
    id: 'clientes-ficha',
    titulo: 'Ficha del cliente',
    rutas: ['/clientes/:id'],
    permiso: { clave: 'clientes.crear' },
    resumen: 'Todo lo del cliente en una página: sus datos, su lista, su crédito, su saldo a favor, lo que debe y su expediente de compras y pagos.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Arriba, sus etiquetas: **Especial**, **Municipal**, **Crédito** con su plazo, **Inactivo**. Y los avisos de lo que falta: «Falta dirección fiscal para FACTURA», «Falta el teléfono para la NOTA».',
          'Si debe vencido: «Deuda vencida $ …: no se le vende sin autorización».',
          '**Lista de precios**, **Crédito** (límite, plazo, cuentas por cobrar y disponible) y **Saldo a favor**.',
          '**Movimientos de saldo**: cada movimiento con su nombre (**Abono**, **Devolución**, **Reintegro de retención**, **Usado para pagar**, **Traspasado a otro cliente**, **Traspaso recibido**, **Ajuste (baja)**), su referencia con el número («Recibo de abono …», «Factura …», «Nota …», «Retención de …»), el saldo después y el usuario. Un clic en «Factura …» o «Nota …» abre el documento (si es de la sucursal que operas) y en «Recibo de abono …» abre el recibo. El saldo a favor no se edita a mano.',
          '**Cuentas por cobrar** (si tiene): los documentos a crédito con saldo, su vencimiento y los días de atraso.',
          '**Expediente**: sus últimos documentos en todas las sucursales (las anuladas marcadas), sus pagos recibidos, sus cotizaciones y sus abonos.',
          '**Archivos del cliente**, al pie: la foto del RIF, la cédula, una carta o lo que haga falta. Se ven desde cualquier sucursal.',
          '**Correos**, al pie: todo lo que se le envió por correo, de todas las sucursales, con la fecha, a quién, quién lo envió (o **Automático**), si salió y qué decía.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'En divisas sin IGTF',
        // La ven la dueña y el contador (rol); en la plantilla, «productos.costos» lo tienen solo ellos dos
        permiso: { clave: 'productos.costos' },
        items: [
          'Dentro del **Saldo a favor** ves la parte **En divisas sin IGTF**: lo que entró en divisas y todavía no pagó el IGTF. Paga el 3 % cuando se usa en una factura o una nota de débito, y al usar el saldo se gasta primero.',
          'En **Movimientos de saldo** sale la columna **En divisas sin IGTF**: lo que cada movimiento sumó o restó a esa parte («sin cambio» si no la tocó) y, debajo, lo que quedó.',
          'En el recibo de un abono, un recuadro encima dice cuánto entró a esa parte. No sale en el papel.',
          'El cajero y el administrador no ven esta parte.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Adjuntar un archivo del cliente',
        pasos: ['En **Archivos del cliente**, pulsa **Adjuntar archivo** (o el botón de la cámara en el teléfono).', 'Elige la foto o el PDF, de hasta 3 MB. Se guarda al elegirlo.', 'Si se adjuntó mal, la dueña lo borra con su motivo.'],
        permiso: { clave: 'clientes.crear', nivel: 'total' },
      },
      {
        tipo: 'lista',
        titulo: 'El expediente',
        items: [
          'Los documentos de tu sucursal son enlaces. Los de otra sucursal salen sin enlace y la ficha lo avisa: se abren desde esa sucursal.',
          'La dueña los abre cambiando de sucursal en la cabecera.',
          'En **Pagos recibidos**, clic en un cobro abre su documento.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Reimprimir el recibo de un abono',
        pasos: ['En **Pagos recibidos**, pulsa el «Abono» con su número (o el número en **Abonos (adelantos)**).', 'Se abre el recibo listo para imprimir, con los datos del original. Desde la segunda impresión dice **REIMPRESIÓN**.', 'Para mandárselo, pulsa **Enviar por correo** al lado del abono (o en el recibo): va a su correo registrado.'],
      },
      {
        tipo: 'lista',
        titulo: 'Botones',
        items: [
          '**Volver** regresa a la lista.',
          '**Editar datos** cambia los datos básicos, también el **Correo**: es el único al que se le envían sus documentos. La auditoría guarda cómo estaban antes, también las notas.',
          '**Lista y crédito** cambia la lista, la clasificación y el crédito. Solo lo ve la dueña, o a quien ella le dé el permiso.',
          '**Traspasar saldo** y **Ajustar saldo** (en **Saldo a favor**, si tiene saldo): ver «Traspasar o ajustar el saldo a favor».',
          '**Desactivar** / **Activar**: con el mismo permiso. **Desactivar** pide confirmar en una ventana: no se le puede vender ni cotizar hasta activarlo de nuevo, y su ficha, su saldo a favor, su crédito y sus documentos quedan igual. **Activar** es directo. Queda auditado.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Factura vencida',
        items: [
          'Cada factura o nota a crédito que vence con saldo genera un aviso en la campana, para la administración de esa tienda y para la dueña, con el teléfono del cliente.',
          'Sale una sola vez por documento. Clic en el aviso abre esta ficha.',
          'Al cliente no se le escribe: la llamada la hace la tienda. La lista de cobranza está en **Reportes**, en **Cuentas por cobrar**.',
        ],
      },
      { tipo: 'nota', texto: 'La ficha de Consumidor Final no se edita ni se desactiva: el punto de venta y las cotizaciones abren con ella.' },
    ],
    manual: '7.2',
    relacionados: ['clientes', 'clientes.nuevo', 'pos.abono', 'reportes', 'notificaciones', 'adjuntos', 'correo'],
    claves: ['ficha', 'saldo a favor', 'expediente', 'cuentas por cobrar', 'deuda vencida', 'recibo de abono', 'movimientos', 'cobranza', 'archivos del cliente', 'correo'],
  },
  {
    id: 'clientes-ficha.credito',
    titulo: 'Lista, clasificación y crédito',
    padre: 'clientes-ficha',
    permiso: { clave: 'clientes.credito' },
    resumen: 'Lo que solo cambia la dueña (o a quien ella le dé el permiso): la lista de precios del cliente, si es contribuyente especial o municipal, y su crédito.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cambiarlo',
        pasos: [
          'En la ficha pulsa **Lista y crédito**.',
          'Elige la **Lista de precios**. Las listas se crean en **Configuración**. Si la del cliente se desactivó, sale como «… (desactivada: elige otra)»: elígele otra.',
          'Elige el **Contribuyente** (Ordinario o Especial). Marca **Contribuyente municipal** si retiene; su **% municipal (vacío = global)** usa el general si lo dejas vacío.',
          'En **Condición** elige **Contado** o **Crédito**. Con crédito, escribe el **Límite ($)** y el **Plazo en días** (lo normal son 20).',
          'Pulsa **Guardar**. Queda auditado con tu usuario.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Un cliente a crédito necesita un límite mayor que cero y un plazo en días.',
          'Con un plazo mayor al normal sale un aviso: queda registrado y la dueña recibe el aviso.',
          'Si el crédito lo asigna otra persona con el permiso, la dueña recibe el aviso. Cambiar la clasificación fiscal también le avisa.',
          'La clasificación fiscal cambia las retenciones al facturar.',
          'Con la lista desactivada, al cliente no se le vende ni se le cotiza. Para reactivarlo, elígele otra lista en esta misma ventana.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'Un cliente a crédito requiere límite en $ mayor que cero y plazo en días (lo normal son 20)', que_hacer: 'Escribe el límite y el plazo.' },
      { mensaje: 'La lista de precios «…» de … está desactivada: cámbiale la lista en «Lista y crédito» y vuelve a activarlo', que_hacer: 'Elígele otra lista en esta ventana y actívalo.' },
    ],
    manual: '7.3',
    relacionados: ['pos.credito'],
    claves: ['crédito', 'límite', 'plazo', 'lista de precios', 'contribuyente especial', 'municipal', 'retención', 'clasificación'],
  },

  {
    id: 'clientes-ficha.saldo',
    titulo: 'Traspasar o ajustar el saldo a favor',
    padre: 'clientes-ficha',
    permiso: { clave: 'clientes.crear', nivel: 'total' },
    resumen: 'Dos correcciones del saldo a favor que hace la dueña (o a quien ella le dé el permiso; los demás piden autorización): pasar a otro cliente el saldo que dejó una nota de crédito, o bajar un saldo que quedó de más.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Traspasar saldo',
        pasos: [
          'En la ficha, en **Saldo a favor**, pulsa **Traspasar saldo**.',
          'Elige la nota de crédito que dejó el saldo: sale cuánto se puede pasar todavía de cada una.',
          'Busca y elige el cliente que lo recibe.',
          'Revisa el **Monto** (viene lleno con lo disponible), escribe el **Motivo** y pulsa **Traspasar**.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Ajustar saldo',
        pasos: [
          'Pulsa **Ajustar saldo**.',
          'Elige el documento donde de verdad se usó ese dinero (una factura, nota o nota de débito de este cliente).',
          'Escribe el **Monto** que baja, el **Motivo** y pulsa **Bajar el saldo**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Solo se traspasa el saldo que dejó una nota de crédito (o el Cambio de una nota de entrega) de este cliente, y nunca más de lo que acreditó.',
          'El traspaso deja dos movimientos enlazados: **Traspasado a otro cliente** en uno y **Traspaso recibido** en el otro, cada uno con el nombre del otro cliente.',
          'La parte del saldo en divisas sin IGTF que trajo esa nota viaja con él.',
          'El ajuste solo baja el saldo: sale como **Ajuste (baja)** con su documento. No mueve caja.',
          'Ninguno es un documento fiscal: no tocan el libro de ventas, el IVA ni el IGTF. Quedan en la auditoría con el motivo.',
          'Si lo hace otro (con el permiso o con una autorización), la dueña recibe el aviso con lo que de verdad se hizo.',
          'Al **Facturar a otro nombre** el traspaso se hace solo: no hace falta repetirlo aquí.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'De la nota de crédito … se pueden traspasar a lo sumo $ …', que_hacer: 'Baja el monto: no se pasa más de lo que dejó esa nota de crédito.' },
      { mensaje: 'El crédito de la nota de crédito … ya se traspasó completo', que_hacer: 'Ese saldo ya está en el otro cliente. Mira sus **Movimientos de saldo**.' },
      { mensaje: '… no es de …: el ajuste se liga a un documento del mismo cliente', que_hacer: 'Elige un documento de este cliente. Para pasar saldo a otro cliente usa **Traspasar saldo**.' },
    ],
    manual: '7.6',
    relacionados: ['autorizacion', 'documentos.otro-nombre'],
    claves: ['traspasar saldo', 'pasar saldo', 'ajustar saldo', 'saldo duplicado', 'pago registrado dos veces', 'saldo a favor'],
  },

  // ─── Zelle y Binance recibidos ──────────────────────────────────────────────
  {
    id: 'pagos-recibidos',
    titulo: 'Zelle y Binance recibidos',
    rutas: ['/pagos-recibidos'],
    permiso: { clave: 'pagos.precargar' },
    resumen: 'Aquí la dueña carga lo que entró a sus cuentas por Zelle y Binance. Así, al cobrar, el cajero ve quién lo envió y el sistema exige el mismo monto.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'La pantalla',
        items: [
          'Dos pestañas: **Recibidos** (lo cargado) y **Cobros por verificar** (cobros cuya referencia no estaba cargada; el número dice cuántos hay).',
          '**Cargar pago** carga uno. **Pegar varios** carga muchos de una vez.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Reglas',
        items: [
          'Zelle y Binance comparten sus referencias: una referencia no se carga dos veces, aunque la segunda vez se elija el otro medio.',
          'El cajero la encuentra aunque cobre con el otro medio: el cobro le avisa que cambie la forma de pago.',
          'La referencia pide el mismo mínimo que al cobrar.',
          'Un pago recibido no se edita ni se borra. Si se cargó mal y está sin usar, se anula y se carga de nuevo.',
          'Un Zelle o Binance cargado también sirve para un abono.',
        ],
      },
      { tipo: 'nota', texto: 'Todo es carga manual: el sistema no se conecta con el banco.' },
    ],
    mensajes: [{ mensaje: 'La referencia debe tener al menos … caracteres (letras o dígitos), igual que al cobrar', que_hacer: 'Copia la referencia completa.' }],
    manual: '8',
    relacionados: ['pos.zelle'],
    claves: ['Zelle', 'Binance', 'USDT', 'pagos recibidos', 'precargar', 'referencia', 'banco', 'cuenta'],
  },
  {
    id: 'pagos-recibidos.recibidos',
    titulo: 'Pestaña Recibidos',
    padre: 'pagos-recibidos',
    resumen: 'Lo cargado, con su medio, su cuenta y su estado: **Sin usar**, **Usado** o **Anulado**.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Cada pago: cuándo se cargó y quién, el medio y la cuenta, la referencia, quién lo envió (con su nota), el monto, el estado y dónde se usó.',
          '**Usado** dice el documento, el cliente, el cajero, y la tienda y la caja donde se usó.',
          'Arriba, los totales: **Total recibido** (o **Total con estos filtros**), el monto y la cantidad de cada estado y, si se cargaron cuentas, **Por cuenta (sin anulados)**. Sirven para cuadrar cada cuenta contra el banco.',
          'Si conciliaste un pago con otro monto, sale **Diferencias con el cobro (banco − cobrado)** y la fila del pago dice «se cobró … · dif. …».',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Filtros',
        items: ['Nombre o referencia, estado, banco o cuenta, la tienda y la caja donde se usó, y las fechas en que entró (**Entró del … al …**).', '**Quitar filtros** los limpia.'],
      },
      {
        tipo: 'pasos',
        titulo: 'Anular un pago mal cargado',
        pasos: ['En la fila del pago sin usar, pulsa **Anular**.', 'Escribe el **Motivo** (al menos 5 letras) y pulsa **Anular**.', 'La referencia queda libre: cárgalo de nuevo con los datos correctos.'],
      },
    ],
    manual: '8.3',
    claves: ['recibidos', 'sin usar', 'usado', 'anulado', 'total recibido', 'por cuenta', 'cuadrar banco'],
  },
  {
    id: 'pagos-recibidos.verificar',
    titulo: 'Pestaña Cobros por verificar',
    padre: 'pagos-recibidos',
    resumen: 'Los cobros por Zelle o USDT cuya referencia no estaba cargada cuando el cajero cobró.',
    bloques: [
      { tipo: 'lista', titulo: 'Qué muestra', items: ['Cada cobro: cuándo y en qué sucursal, el medio, la referencia, el monto, el documento, el cliente y el cajero.'] },
      {
        tipo: 'pasos',
        titulo: 'Verificar un cobro',
        pasos: [
          'Revisa si el pago está en el banco.',
          'Si está, cárgalo con **Cargar pago**: se concilia solo si el monto coincide; si no, el sistema avisa y eliges conciliar anotando la diferencia, corregir el monto o dejarlo sin cargar.',
          'O confírmalo a mano con **Está en el banco**.',
        ],
      },
      { tipo: 'nota', texto: 'Cuando no queda ninguno, dice «Nada por verificar».' },
    ],
    manual: '8.4',
    claves: ['por verificar', 'conciliar', 'está en el banco', 'verificar Zelle'],
  },
  {
    id: 'pagos-recibidos.cargar',
    titulo: 'Cargar un pago',
    padre: 'pagos-recibidos',
    resumen: 'Carga un Zelle o un Binance tal como aparece en tu banco o en Binance. La referencia no se puede repetir.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cargar',
        pasos: [
          'Pulsa **Cargar pago**.',
          'Elige el **Medio** (Zelle o Binance / USDT) y la **Fecha en que entró** (viene la de hoy).',
          'Escribe **Quién lo envió**, el **Banco o cuenta que lo recibió (opcional)**, el **Monto ($)** y la **Referencia**.',
          'La **Nota (opcional)** es libre. Pulsa **Cargar**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'La cuenta separa lo que entró a cada una, por ejemplo «ZELLE CITI MG» o «BINANCE». El campo ofrece las que ya usaste y deja puesta la última para la carga siguiente.',
          'Si ya había un cobro con esa referencia y el mismo monto, el pago queda conciliado con ese cobro.',
          'Si el monto no coincide (el banco dice $ 428,00 y se cobró $ 427,50), el pago **no se carga**: el resultado dice el documento, la tienda, el cajero, la fecha y los dos montos. Elige **Conciliar y anotar la diferencia** (queda usado con ese cobro y la diferencia anotada, sin mover dinero), **Corregir el monto** (vuelve al formulario con los datos) o **Dejar sin cargar** (el cobro sigue por verificar).',
          'Si algo hay que revisar, sale el **Resultado de la carga** con cada referencia. **Cerrar** lo quita.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'Ya estaba cargado', que_hacer: 'Esa referencia ya está: búscala en **Recibidos**.' },
      { mensaje: 'No se cargó: ya se cobró por otro monto', que_hacer: 'El cajero ya cobró esa referencia por otro monto. Si el banco tiene razón, pulsa **Conciliar y anotar la diferencia**; si escribiste mal el monto, **Corregir el monto**; si hay que revisarlo con el cajero, **Dejar sin cargar**.' },
    ],
    manual: '8.1',
    claves: ['cargar pago', 'Zelle', 'Binance', 'referencia', 'cuenta'],
  },
  {
    id: 'pagos-recibidos.pegar',
    titulo: 'Pegar varios pagos',
    padre: 'pagos-recibidos',
    resumen: 'Carga muchos pagos de una vez, pegados desde el banco o desde una hoja de cálculo.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Pegar',
        pasos: [
          'Pulsa **Pegar varios**.',
          'Elige el **Medio**.',
          'En **Pagos** pega un pago por línea: **Nombre ; Monto ; Referencia**, separados por punto y coma o copiados de una hoja de cálculo.',
          'Revisa la lista de abajo. Una línea con la referencia corta sale marcada «Referencia muy corta»: corrígela. Mientras haya una línea con error, no se carga.',
          'Pulsa **Cargar … pago(s)**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Al terminar',
        items: ['Sale el **Resultado de la carga** de cada referencia: **Cargado**, **Conciliado con un cobro**, **Ya estaba cargado** o **No se cargó: ya se cobró por otro monto** (con los botones para decidir).', 'Por esta vía los pagos entran sin banco o cuenta.'],
      },
    ],
    manual: '8.2',
    claves: ['pegar', 'varios pagos', 'hoja de cálculo', 'carga masiva'],
  },

  // ─── Devoluciones ───────────────────────────────────────────────────────────
  {
    id: 'devoluciones',
    titulo: 'Devoluciones',
    rutas: ['/devoluciones'],
    permiso: { clave: 'devoluciones.registrar' },
    resumen: 'Toda devolución se hace contra el documento de la venta. De una factura sale una nota de crédito; de una nota de entrega, un **Cambio**. El crédito queda como saldo a favor del cliente.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Reglas',
        items: [
          'De una **factura** sale una **nota de crédito** fiscal, con su número de control. De una **nota de entrega** sale un **Cambio**, documento interno no fiscal.',
          'No se devuelve dinero: el crédito queda como **saldo a favor**, que el cliente puede usar de inmediato como forma de pago.',
          'El crédito de un **Cambio** vuelve a la parte del saldo que entró en divisas, hasta lo que la nota pagó en efectivo $, Zelle, USDT o con esa parte del saldo: la nota nunca pagó IGTF, así que lo paga cuando ese saldo cubra una factura. El de una **nota de crédito** no.',
          'Plazo: 30 días desde la venta. Pasado el plazo se necesita el permiso o la autorización de un administrador o de la dueña.',
          'Solo se devuelve lo ya **entregado** y no devuelto antes.',
          'El cajero necesita su caja abierta para cualquier devolución, aunque el cliente no se lleve nada. El administrador y la dueña solo la necesitan si el cliente se lleva otra cosa.',
          'Las cantidades respetan la fracción mínima del producto. Una pieza de vidrio o un producto con detalle se devuelve por unidades enteras y vuelve a su lista.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Registrar una devolución',
        pasos: [
          'En **1 · Documento origen**, busca por número, control, cliente o RIF y elige el documento. Solo salen los que tienen mercancía entregada y no devuelta, a lo sumo los 10 más recientes: si llega a 10, lo avisa.',
          'En **Devolver (u)** escribe cuántas unidades se devuelven de cada renglón. Marca **Dañado** si la mercancía llega dañada.',
          'En **2 · Registro**, revisa el **Almacén receptor** (viene puesto el principal) y escribe el **Motivo**.',
          'Revisa los totales, como en el punto de venta: base e IVA (si es factura) en $ y Bs; el total y el **Crédito a saldo a favor** en Bs, $ a BCV y $ divisa, con la tasa del documento origen.',
          'Si ves los libros, mira **Así queda en los libros fiscales**: la nota de crédito resta en el libro de ventas de este mes, sin IGTF ni retención (el Cambio no entra), y quien ve costos ve lo que entra al libro de inventario real y al fiscal.',
          'Pulsa **Registrar devolución**.',
          'La ventana que se queda en pantalla separa el **Crédito de esta nota de crédito** (o de este cambio) del **Saldo a favor total del cliente**, que suma lo que ya tenía (sus Bs son de referencia, a la tasa de hoy). Pulsa **Imprimir nota de crédito** o **Imprimir cambio**, y después **Listo**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'El documento elegido',
        items: [
          'Cada renglón dice lo **Entregado**, lo **Devuelto** y lo **Devolvible**.',
          'Si pasó el plazo, sale **fuera de plazo: pide autorización**.',
          '**Otro documento** vuelve a la búsqueda.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Material dañado',
        items: [
          'Lo marcado **Dañado** entra al almacén y sale de la existencia en la misma operación, con un ajuste **Daño** ligado a la devolución. Nunca queda disponible para la venta.',
          'Si quien registra no aprueba ajustes, la dueña recibe el aviso.',
          'Ese ajuste no da el aviso «… bajó del mínimo»: saca lo mismo que la devolución acaba de meter, y lo disponible queda igual.',
          'La ventana final dice en qué quedó el ajuste, con **Ver ajustes**.',
          'Una pieza de vidrio dañada pasa a merma.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'La lista de abajo',
        items: [
          '**Notas de crédito, débito y cambios**: los documentos de la sucursal, los más recientes primero, con filtro de fechas y búsqueda por número, origen o cliente.',
          'Cada fila dice el documento, el origen (y lo que se llevó el cliente), el cliente, la fecha, el total, el crédito y el motivo. Una nota de débito dice lo pendiente por cobrar.',
          'El botón de la impresora reimprime la hoja.',
          'Muestra las 50 más recientes: para ver las demás, acota las fechas o busca.',
        ],
      },
      { tipo: 'nota', texto: 'La nota de crédito no cambia la retención de la factura: el comprobante del cliente sigue por el monto original. Si la nota deja la factura en cero y el comprobante no ha llegado, esa retención pasa a **Revisar**.' },
      { tipo: 'nota', texto: 'Un documento a Consumidor Final no acumula saldo a favor ni admite que se lleve otra cosa: solo procede como devolución de dinero, con autorización.' },
      { tipo: 'nota', texto: 'El reporte por cliente y fechas está en **Reportes**, en **Cambios y devoluciones**.' },
    ],
    mensajes: [
      { mensaje: 'Debes abrir tu sesión de caja antes de vender o cobrar (o la caja está cerrando)', que_hacer: 'El cajero devuelve con su caja abierta: ábrela en **Caja** y registra de nuevo.' },
      { mensaje: '… se vende en múltiplos de …: no se pueden devolver …', que_hacer: 'Corrige la cantidad.' },
      { mensaje: '…: solo quedan … unidades devolvibles', que_hacer: 'Devuelve como máximo lo que dice **Devolvible**.' },
      { mensaje: 'Nada devolvible con ese criterio (¿ya se entregó la mercancía?)', que_hacer: 'Solo se devuelve lo entregado: revisa el documento o busca otro.' },
      { mensaje: '… está anulado (hoja dañada): devuelve contra el documento que lo sustituyó', que_hacer: 'Busca el documento que tomó su lugar.' },
      { mensaje: 'Se muestran las 50 más recientes: acota las fechas o busca por número o cliente para ver las demás', que_hacer: 'Acota las fechas o busca.' },
      { mensaje: 'Se muestran los 10 documentos más recientes con ese criterio: escribe el número completo, el control o el RIF para encontrar otro', que_hacer: 'Escribe el número completo, el control o el RIF.' },
      { mensaje: 'Esa clave de operación ya se usó en otra operación: vuelve a iniciar la devolución', que_hacer: 'Pulsa **Otro documento**, elige de nuevo el documento y registra la devolución.' },
      { mensaje: 'El lote de esa unidad es del almacén …, que está desactivado: reactívalo para devolverla sana, o regístrala dañada', que_hacer: 'Que la dueña active ese almacén, o marca la unidad como **Dañado**.' },
    ],
    manual: '20',
    relacionados: ['documentos', 'entregas', 'ajustes', 'retenciones', 'autorizacion'],
    claves: ['devolución', 'devolver', 'nota de crédito', 'cambio', 'saldo a favor', 'dañado', 'fuera de plazo', 'almacén receptor'],
  },
  {
    id: 'devoluciones.otra-cosa',
    titulo: 'Si el cliente se lleva otra cosa',
    padre: 'devoluciones',
    resumen: 'En el mismo acto de la devolución, el cliente se lleva otros productos, pagados con el crédito de lo que devuelve.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'De una factura',
        pasos: [
          'Después de indicar lo que devuelve, marca **El cliente se lleva otra cosa: factura nueva**.',
          'Busca y agrega lo que se lleva, con su presentación y su cantidad.',
          'Si el cliente es contribuyente especial, elige la retención de la factura nueva.',
          'Pulsa **Registrar nota de crédito y factura nueva**. Salen la nota de crédito y una factura nueva, a precios y tasa de hoy. No se abre ninguna impresión sola: la ventana trae **1 · Imprimir nota de crédito** y **2 · Imprimir factura nueva**, cada uno con su N° de control. Imprímelas en ese orden, cada una en su hoja preimpresa.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'De una nota',
        pasos: [
          'Marca **Cambio directo: despachar ya lo que se lleva**.',
          'Busca y agrega lo que se lleva.',
          'Pulsa **Registrar cambio y despachar**. Salen el **Cambio** y una nota de despacho pagada con su crédito. La hoja CAMBIO muestra **RECIBIMOS** y **DESPACHAMOS**, con la diferencia.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'En los dos casos',
        items: [
          'Si lo que se lleva vale más que el crédito, el botón dice **Cobrar diferencia … y registrar** y abre el cobro (con IGTF si es factura y paga en divisas).',
          'Si vale menos, el resto queda como saldo a favor. Con la autorización de la dueña, ese resto también se puede devolver en dinero.',
          'Si vale lo mismo, dice «El crédito cubre exactamente lo que se lleva».',
          'Lo que está en la tienda se entrega de una vez.',
          'Si el cliente paga una diferencia y tiene deuda vencida, se pide la autorización de cliente vencido, como en cualquier venta.',
        ],
      },
    ],
    mensajes: [{ mensaje: '…: no hay … unidades en buen estado para despachar', que_hacer: 'Baja la cantidad o elige otro producto.' }],
    manual: '20.2',
    relacionados: ['pos.cobro', 'devoluciones.dinero'],
    claves: ['cambio', 'factura nueva', 'despachar', 'diferencia', 'se lleva otra cosa', 'reemplazo'],
  },
  {
    id: 'devoluciones.dinero',
    titulo: 'Devolver el dinero',
    padre: 'devoluciones',
    resumen: 'Es la excepción: solo con la autorización de la dueña, el crédito no va al saldo a favor y se le devuelve al cliente por transferencia.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Pedirlo',
        pasos: [
          'Al registrar la devolución, marca **Excepción: devolver el dinero**.',
          'Pulsa el botón de registrar. El sistema pide la autorización de la dueña (con su clave o a distancia).',
          'El crédito no va al saldo a favor: queda una devolución de dinero **pendiente**.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Pagarlo',
        permiso: { clave: 'devoluciones.reintegro-dinero' },
        pasos: [
          'Arriba en **Devoluciones** sale **Dinero por devolver**, con cada devolución autorizada.',
          'Haz la transferencia desde tu banco.',
          'Pulsa **Registrar transferencia**, escribe **Banco** y **Referencia** y pulsa **Registrar como pagada**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Nada sale de la caja: el cierre no cambia.',
          'El sistema no se conecta con el banco: la transferencia se comprueba a mano.',
          'Tope: en dinero nunca se devuelve más de lo que el cliente pagó de ese documento, descontando lo ya devuelto o acreditado. Si no alcanza, la casilla sale desactivada y el crédito queda como saldo a favor.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'No procede: de este documento el cliente ha pagado menos de lo que se devolvería (en dinero, hasta $ …)', que_hacer: 'Registra la devolución sin devolver dinero: el crédito queda como saldo a favor y le sirve para pagar esa misma factura.' },
      { mensaje: 'Todo el crédito se usa en lo que el cliente se lleva: no queda dinero que devolver', que_hacer: 'Desmarca la excepción.' },
    ],
    manual: '20.3',
    relacionados: ['autorizacion'],
    claves: ['devolver dinero', 'reintegro', 'transferencia', 'dinero por devolver', 'excepción'],
  },
  {
    id: 'devoluciones.nota-debito',
    titulo: 'Nota de débito',
    padre: 'devoluciones',
    permiso: { clave: 'devoluciones.modo-cambio' },
    resumen: 'Para cobrar una diferencia (precio, flete) sobre una factura ya emitida. Lleva su número y su número de control.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Emitirla',
        pasos: [
          'Pulsa **Nota de débito**.',
          'Busca la factura de origen (número, control o cliente) y elígela. Si la búsqueda trae más de las que muestra, lo dice con el total. **cambiar** elige otra.',
          'En **Agregar producto del cargo…** busca el producto y agrégalo. Escribe la cantidad y el precio en dólares (**$ divisa c/u**). Los dos tienen que ser mayores que cero: con un cero, **Emitir ND** no se activa.',
          'Escribe el **Motivo** y pulsa **Emitir ND**.',
          'En la ventana que se queda en pantalla pulsa **Imprimir nota de débito** y después **Listo**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Suma en el libro de ventas y queda por cobrar a crédito: se cobra desde **Documentos** con **Cobrar saldo**.',
          'Vence con el plazo de crédito del cliente, contado desde el día en que se emite. Si el cliente no tiene plazo, con el plazo normal de crédito.',
          'Si se paga en divisas, lleva IGTF.',
          'Va con las tasas de la factura de origen.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Es un documento fiscal: no se edita ni se borra.' },
    ],
    mensajes: [
      { mensaje: 'La nota de débito solo aplica a FACTURAS', que_hacer: 'Elige una factura.' },
      { mensaje: 'La nota de débito tiene que cobrar algo: escribe un precio mayor que cero', que_hacer: 'Escribe un precio mayor que cero: una nota en cero no se emite.' },
      { mensaje: 'No se emite nota de débito a Consumidor Final: no lleva cuenta por cobrar', que_hacer: 'Busca una factura de un cliente identificado.' },
    ],
    manual: '20.5',
    relacionados: ['documentos.cobrar', 'libros'],
    claves: ['nota de débito', 'ND', 'cargo', 'flete', 'diferencia de precio'],
  },
];
